import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import printerService from '../../services/printerService';
import billingService from '../../services/billingService';
import ConfiguracionSecuenciaModal from './ConfiguracionSecuenciaModal';
import RegalosModal from './RegalosModal';
import { Regalo } from '../publico/Iconos';



// ====================================================================
// 1. Funciones de Ayuda (Definiciones de formato)
// ====================================================================

const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num || 0);
};

const formatDate = (dateString) => {
    try {
        if (!dateString) return 'Fecha no disponible';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        return date.toLocaleDateString('es-MX', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch (e) {
        return dateString;
    }
};

// Constantes para áreas táctiles (mínimo 44x44px)
const TOUCH_MIN_SIZE = '44px';

const PuntosVenta = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isSuperAdmin = roleName === 'SUPER_ADMIN' || user?.is_superuser;
    const isAdminFastFood = roleName === 'ADMIN_FAST_FOOD';
    const isCook = roleName === 'COOK' || user?.role === 'COOK' || user?.role_details?.description?.toLowerCase()?.includes('cocinero');
    const isAdmin = isSuperAdmin || isAdminFastFood || user?.role === 'admin';
    const canAccessFeria = isAdmin || isCook;

    // =====================================
    // 1. ESTADO DE DATOS Y CARGA
    // =====================================
    const [currentShift, setCurrentShift] = useState(null);
    const [currentLocalShift, setCurrentLocalShift] = useState(null);
    const [currentFeriaShift, setCurrentFeriaShift] = useState(null);
    const [checkingShift, setCheckingShift] = useState(true);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processingOrder, setProcessingOrder] = useState(false);
    const [printStep, setPrintStep] = useState(''); // animación de carga
    const [orderSuccessNotification, setOrderSuccessNotification] = useState('');
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    // Alto disponible para el POS: la pantalla menos lo que haya encima (p. ej. avisos de cobranzas),
    // para que el total y el botón de cobrar siempre se vean en tablets de 11" horizontales
    const posViewRef = useRef(null);
    const [posViewTop, setPosViewTop] = useState(0);
    const [showOrderDetails, setShowOrderDetails] = useState(false);
    const [editingNoteForItem, setEditingNoteForItem] = useState(null);
    const [noteText, setNoteText] = useState('');

    // 2. ESTADO DEL PUNTO DE VENTA
    const [cart, setCart] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTable, setSelectedTable] = useState('');
    const [discountCode, setDiscountCode] = useState('');
    const [orderDiscountPercentage, setOrderDiscountPercentage] = useState(0);
    const [isEmployeeDiscount, setIsEmployeeDiscount] = useState(false);
    const [appliedDiscount, setAppliedDiscount] = useState(null);

    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [transferReference, setTransferReference] = useState('');
    const [documentType, setDocumentType] = useState('factura'); // 'factura' = SRI KoaDoc, 'nota_entrega' = Local
    const [showDocTypeModal, setShowDocTypeModal] = useState(false);
    const [pendingIsFeria, setPendingIsFeria] = useState(false);

    // Pagos Divididos (Mixtos)
    const [split1Method, setSplit1Method] = useState('efectivo');
    const [split1Currency, setSplit1Currency] = useState('USD');
    const [split1Amount, setSplit1Amount] = useState('');
    
    const [split2Method, setSplit2Method] = useState('transferencia');
    const [split2Currency, setSplit2Currency] = useState('COP');
    const [split2Amount, setSplit2Amount] = useState('');

    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showSequenceModal, setShowSequenceModal] = useState(false);
    const [showRegalosModal, setShowRegalosModal] = useState(false);


    // 3.5 ESTADO DE CALCULADORA DE VUELTO
    const [cashGiven, setCashGiven] = useState(null);
    const [inputCash, setInputCash] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [exchangeRate, setExchangeRate] = useState(parseFloat(localStorage.getItem('usdExchangeRate')) || 4000);

    // 3. ESTADO DE CLIENTES
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        email: '',
        password: 'Password123!',
        password_confirmation: 'Password123!',
        first_name: '',
        last_name: '',
        phone: '',
        address: '',
        city: '',
        cedula: ''
    });

    // 4. ESTADO PARA TOPPINGS
    const [availableToppings, setAvailableToppings] = useState([]);
    const [showToppingsModal, setShowToppingsModal] = useState(false);
    const [activeToppingsItemIndex, setActiveToppingsItemIndex] = useState(null);

    // 5. ESTADO PARA VARIABLES
    const [showVariablesModal, setShowVariablesModal] = useState(false);
    const [selectedProductForVariables, setSelectedProductForVariables] = useState(null);
    const [currentVariablesSelection, setCurrentVariablesSelection] = useState({});

    // 6. ESTADO PARA ÓRDENES TEMPORALES / EN ESPERA (PARKED ORDERS)
    const [heldOrders, setHeldOrders] = useState(() => {
        try {
            const saved = localStorage.getItem('fastbox_held_orders');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [showHeldOrdersModal, setShowHeldOrdersModal] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem('fastbox_held_orders', JSON.stringify(heldOrders));
        } catch (e) {
            console.error('Error saving held orders:', e);
        }
    }, [heldOrders]);

    const handleHoldCurrentOrder = () => {
        if (cart.length === 0) {
            alert('El carrito está vacío. Agrega productos antes de poner la orden en espera.');
            return;
        }

        const orderSnapshot = {
            id: 'HOLD-' + Date.now().toString(36).toUpperCase(),
            held_at: new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }),
            cart: [...cart],
            selectedCustomer: selectedCustomer ? { ...selectedCustomer } : null,
            customerSearch: customerSearch,
            selectedTable: selectedTable,
            orderDiscountPercentage: orderDiscountPercentage,
            appliedDiscount: appliedDiscount,
            isEmployeeDiscount: isEmployeeDiscount,
            discountCode: discountCode,
            paymentMethod: paymentMethod,
            currency: currency,
            documentType: documentType,
            total: calculateTotal
        };

        setHeldOrders(prev => [orderSnapshot, ...prev]);

        // Limpiar la pantalla actual para atender al siguiente cliente
        setCart([]);
        setSelectedCustomer(null);
        setCustomerSearch('');
        setSelectedTable('');
        setOrderDiscountPercentage(0);
        setAppliedDiscount(null);
        setIsEmployeeDiscount(false);
        setDiscountCode('');
        setCashGiven(null);
        setInputCash('');
        setShowOrderDetails(false);

        alert(`⏸️ Orden #${orderSnapshot.id} puesta en espera.\nPuedes continuar atendiendo a otros clientes.`);
    };

    const handleRestoreHeldOrder = (heldOrder) => {
        if (cart.length > 0) {
            const confirmOverwrite = window.confirm('Ya tienes productos en el carrito actual. ¿Deseas reemplazar el carrito con la orden en espera?');
            if (!confirmOverwrite) return;
        }

        setCart(heldOrder.cart || []);
        setSelectedCustomer(heldOrder.selectedCustomer || null);
        setCustomerSearch(heldOrder.customerSearch || '');
        setSelectedTable(heldOrder.selectedTable || '');
        setOrderDiscountPercentage(heldOrder.orderDiscountPercentage || 0);
        setAppliedDiscount(heldOrder.appliedDiscount || null);
        setIsEmployeeDiscount(heldOrder.isEmployeeDiscount || false);
        setDiscountCode(heldOrder.discountCode || '');
        setPaymentMethod(heldOrder.paymentMethod || 'efectivo');
        setCurrency(heldOrder.currency || 'USD');
        setDocumentType(heldOrder.documentType || 'factura');

        // Remover de la lista de órdenes en espera
        setHeldOrders(prev => prev.filter(o => o.id !== heldOrder.id));
        setShowHeldOrdersModal(false);
    };

    const handleDeleteHeldOrder = (heldId, e) => {
        if (e) e.stopPropagation();
        if (window.confirm('¿Estás seguro de eliminar esta orden en espera?')) {
            setHeldOrders(prev => prev.filter(o => o.id !== heldId));
        }
    };

    // =====================================
    // 4. EFECTOS - CARGA INICIAL DE DATOS Y RESPONSIVIDAD
    // =====================================
    // Inject custom scrollbar styles
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            ::-webkit-scrollbar {
                width: 8px;
                height: 8px;
            }
            ::-webkit-scrollbar-track {
                background: #f1f1f1; 
            }
            ::-webkit-scrollbar-thumb {
                background: var(--border-color); 
                border-radius: 4px;
            }
            ::-webkit-scrollbar-thumb:hover {
                background: var(--border-color); 
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                const [localRes, feriaRes] = await Promise.allSettled([
                    api.get('/api/pos/shifts/current/', {
                        baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                        params: { is_feria: false }
                    }),
                    api.get('/api/pos/shifts/current/', {
                        baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                        params: { is_feria: true }
                    })
                ]);
                
                if (isMounted) {
                    const extractOpenShift = (res) => {
                        if (res.status !== 'fulfilled' || !res.value?.data) return null;
                        const data = res.value.data;
                        if (data.shift && data.shift.id && data.shift.status === 'open') return data.shift;
                        if (data.id && data.status === 'open') return data;
                        return null;
                    };

                    const localData = extractOpenShift(localRes);
                    const feriaData = extractOpenShift(feriaRes);
                    
                    setCurrentLocalShift(localData);
                    setCurrentFeriaShift(feriaData);
                    
                    // El POS se habilita si la caja normal está abierta (para cualquier usuario)
                    // o si la caja de ferias está abierta (exclusivamente para usuarios con acceso a ferias: Admin / Cocinero)
                    const activeShiftForUser = localData || (canAccessFeria ? feriaData : null);
                    setCurrentShift(activeShiftForUser);
                }
            } catch (err) {
                console.warn('Error fetching shifts:', err);
            } finally {
                if (isMounted) {
                    setCheckingShift(false);
                }
            }

            try {
                const productsRes = await api.get('/api/menu/products/', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });

                if (!isMounted) return;

                const loadedProducts = productsRes.data.results || productsRes.data || [];
                loadedProducts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setProducts(loadedProducts);

            } catch (err) {
                console.error('Error cargando productos:', err);
            }

            try {
                const categoriesRes = await api.get('/api/menu/categories/', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });

                if (!isMounted) return;

                const loadedCategories = categoriesRes.data.results || categoriesRes.data || [];
                loadedCategories.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setCategories(loadedCategories);

            } catch (err) {
                console.error('Error cargando categorías:', err);
            }

            try {
                const promosRes = await api.get('/api/menu/combos/?is_promotion=true', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });

                if (!isMounted) return;

                const loadedPromos = promosRes.data.results || promosRes.data || [];
                setPromotions(loadedPromos.filter(p => p.is_active));

            } catch (err) {
                console.error('Error cargando promociones:', err);
            }

            try {
                const tablesRes = await api.get('/api/pos/tables/', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });

                if (!isMounted) return;
                setTables(tablesRes.data.results || tablesRes.data || []);

            } catch (err) {
                console.warn('Mesas no disponibles');
                if (isMounted) {
                    setTables([]);
                }
            }

            try {
                const toppingsRes = await api.get('/api/menu/toppings/', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                if (isMounted) {
                    const data = toppingsRes.data.results || toppingsRes.data;
                    const arr = Array.isArray(data) ? data : [];
                    setAvailableToppings(arr.filter(t => t.is_active));
                }
            } catch (err) {
                console.warn('Toppings no disponibles', err);
            }

            if (isMounted) {
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [canAccessFeria]);

    // Medir dónde empieza la vista del POS (cambia si aparece/desaparece un aviso arriba).
    // La vista no existe mientras carga ("Cargando..."), por eso se vuelve a medir al terminar de cargar.
    const measurePosViewTop = useRef(null);
    measurePosViewTop.current = () => {
        if (!posViewRef.current) return;
        const top = Math.max(0, Math.round(posViewRef.current.getBoundingClientRect().top + window.scrollY));
        setPosViewTop((prev) => (prev === top ? prev : top));
    };
    useLayoutEffect(() => { measurePosViewTop.current(); }, [loading, checkingShift, screenWidth]);
    useEffect(() => {
        const measure = () => measurePosViewTop.current();
        window.addEventListener('resize', measure);
        // El aviso de cobranzas puede aparecer después: observar el contenedor principal
        const container = document.querySelector('.main-content') || document.body;
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (observer) observer.observe(container);
        return () => {
            window.removeEventListener('resize', measure);
            if (observer) observer.disconnect();
        };
    }, []);

    // Detectar tamaño de pantalla
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        handleResize(); // Ejecutar al inicio
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // =====================================
    // 5. LÓGICA DEL CARRITO
    // =====================================
    const handleProductClick = (product, isPromo = false, promoQuantity = 1, promoDiscountPct = 0, promoName = '') => {
        if (!isPromo && product.variables && product.variables.length > 0) {
            setSelectedProductForVariables({ product, isPromo, promoQuantity, promoDiscountPct, promoName });
            const initialSelection = {};
            product.variables.forEach(v => {
                // Si la variable es obligatoria (is_required !== false), preseleccionamos la primera opción activa.
                // Si es opcional, dejamos null para que el cliente elija si desea agregarla o no.
                const isRequired = v.is_required !== false;
                const defaultOption = isRequired && v.options && v.options.length > 0 && v.options[0].is_active ? v.options[0] : null;
                initialSelection[v.id] = defaultOption;
            });
            setCurrentVariablesSelection(initialSelection);
            setShowVariablesModal(true);
        } else {
            addToCart(product, isPromo, promoQuantity, promoDiscountPct, promoName);
        }
    };

    const confirmVariablesSelection = () => {
        const { product, isPromo, promoQuantity, promoDiscountPct, promoName } = selectedProductForVariables;
        
        // Validar únicamente que las variables OBLIGATORIAS estén seleccionadas
        const missingVariables = product.variables.filter(v => (v.is_required !== false) && !currentVariablesSelection[v.id]);
        if (missingVariables.length > 0) {
            alert(`Por favor selecciona una opción obligatoria para: ${missingVariables.map(v => v.name).join(', ')}`);
            return;
        }

        // Solo agregar variables que tengan una opción seleccionada (las opcionales no seleccionadas se omiten)
        const selectedVariables = product.variables
            .filter(v => currentVariablesSelection[v.id])
            .map(v => ({
                variable_id: v.id,
                variable: v.name,
                option_id: currentVariablesSelection[v.id].id,
                option: currentVariablesSelection[v.id].name,
                extra_price: parseFloat(currentVariablesSelection[v.id].extra_price) || 0
            }));

        addToCart(product, isPromo, promoQuantity, promoDiscountPct, promoName, selectedVariables);
        setShowVariablesModal(false);
        setSelectedProductForVariables(null);
        setCurrentVariablesSelection({});
    };

    const addToCart = useCallback((product, isPromo = false, promoQuantity = 1, promoDiscountPct = 0, promoName = '', selectedVariables = []) => {
        setCart(prevCart => {
            const variablesPriceAdjustment = selectedVariables.reduce((total, v) => total + v.extra_price, 0);
            const existingItemIndex = prevCart.findIndex(item => 
                item.product_id === product.id && 
                item.isPromo === isPromo &&
                item.name === (isPromo ? promoName : product.name) &&
                !(product.max_toppings > 0) &&
                !(selectedVariables && selectedVariables.length > 0) &&
                !(item.selectedVariables && item.selectedVariables.length > 0)
            );
            if (existingItemIndex >= 0) {
                const newCart = [...prevCart];
                newCart[existingItemIndex] = {
                    ...newCart[existingItemIndex],
                    quantity: newCart[existingItemIndex].quantity + (isPromo ? promoQuantity : 1)
                };
                return newCart;
            } else {
                return [...prevCart, {
                    cartItemId: Date.now() + '-' + Math.random().toString(36).substring(7),
                    product_id: product.id,
                    combo_id: isPromo ? (product.promoComboId || null) : null,
                    name: isPromo ? promoName : product.name,
                    description: product.description || '',
                    price: parseFloat(product.price) + variablesPriceAdjustment,
                    quantity: isPromo ? promoQuantity : 1,
                    image: isPromo ? product.promoImage : product.image,
                    note: isPromo ? 'Promo aplicada' : '',
                    discount_percentage: isPromo ? promoDiscountPct : 0,
                    isPromo: isPromo,
                    max_toppings: isPromo ? 0 : (product.max_toppings || 0),
                    selectedToppings: [],
                    selectedVariables: selectedVariables
                }];
            }
        });
    }, []);

    const handleAddPromo = async (promo) => {
        try {
            const res = await api.get(`/api/menu/combos/${promo.id}/products/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            const promoProducts = res.data;
            if (promoProducts.length > 0) {
                const comboProduct = promoProducts[0];
                const realProduct = comboProduct.product;
                const quantity = comboProduct.quantity;
                
                const originalTotal = realProduct.price * quantity;
                let discountPct = 0;
                if (originalTotal > 0 && promo.price < originalTotal) {
                    discountPct = ((originalTotal - promo.price) / originalTotal) * 100;
                }
                
                // Agregamos imagen de la promo al producto temporalmente
                realProduct.promoImage = promo.image;
                // Para puntos de fidelidad: el backend sabe de qué promo vino la línea
                realProduct.promoComboId = promo.id;
                
                addToCart(realProduct, true, quantity, discountPct, promo.name);
            } else {
                alert("Esta promoción no tiene productos asociados.");
            }
        } catch(e) {
            console.error("Error al cargar la promo", e);
        }
    };

    const removeFromCart = useCallback((itemToRemove) => {
        setCart(prevCart => prevCart.filter(item => 
            item.cartItemId !== itemToRemove.cartItemId
        ));
    }, []);

    const updateQuantity = useCallback((itemToUpdate, delta) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === itemToUpdate.cartItemId) {
                    const newQuantity = Math.max(1, item.quantity + delta);
                    return { ...item, quantity: newQuantity };
                }
                return item;
            });
        });
    }, []);

    const updateItemDiscount = useCallback((itemToUpdate, percentage) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === itemToUpdate.cartItemId) {
                    return { ...item, discount_percentage: Math.max(0, Math.min(100, percentage)) };
                }
                return item;
            });
        });
    }, []);

    const handleAddNote = (itemToNote) => {
        const item = cart.find(i => i.cartItemId === itemToNote.cartItemId);
        setEditingNoteForItem(itemToNote.cartItemId);
        setNoteText(item?.note || '');
    };

    const saveNote = () => {
        if (!editingNoteForItem) return;

        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === editingNoteForItem) {
                    return { ...item, note: noteText.trim() };
                }
                return item;
            });
        });

        setEditingNoteForItem(null);
        setNoteText('');
    };

    const cancelNote = () => {
        setEditingNoteForItem(null);
        setNoteText('');
    };

    // =====================================
    // 6. CÁLCULOS DE PRECIOS
    // =====================================
    const calculateSubtotal = useMemo(() => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    }, [cart]);

    const calculateItemDiscounts = useMemo(() => {
        return cart.reduce((total, item) => {
            const percentage = parseFloat(item.discount_percentage) || 0;
            return total + (item.price * (percentage / 100) * item.quantity);
        }, 0);
    }, [cart]);

    const calculateOrderDiscount = useMemo(() => {
        const subtotalAfterItemDiscounts = calculateSubtotal - calculateItemDiscounts;
        const percentage = parseFloat(orderDiscountPercentage) || 0;
        return subtotalAfterItemDiscounts * (percentage / 100);
    }, [calculateSubtotal, calculateItemDiscounts, orderDiscountPercentage]);

    const calculateDiscountAmount = useMemo(() => {
        // También mantenemos appliedDiscount por si acaso
        let oldDiscount = 0;
        if (appliedDiscount) {
            const subtotal = calculateSubtotal - calculateItemDiscounts;
            if (appliedDiscount.discount_type === 'percentage') {
                oldDiscount = subtotal * (parseFloat(appliedDiscount.discount_value) / 100);
            } else if (appliedDiscount.discount_type === 'fixed_amount') {
                oldDiscount = Math.min(parseFloat(appliedDiscount.discount_value), subtotal);
            }
        }
        return calculateItemDiscounts + calculateOrderDiscount + oldDiscount;
    }, [calculateItemDiscounts, calculateOrderDiscount, appliedDiscount, calculateSubtotal]);

    const calculateTotal = useMemo(() => {
        const subtotal = calculateSubtotal;
        const discount = calculateDiscountAmount;
        return (subtotal - discount);
    }, [calculateSubtotal, calculateDiscountAmount]);

    // =====================================
    // 7. LÓGICA DE DESCUENTOS
    // =====================================
    const handleApplyDiscount = async () => {
        if (!discountCode) return;
        try {
            const response = await api.post('/api/pos/discounts/validate/', { code: discountCode }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            if (response.data.valid) {
                setAppliedDiscount(response.data.discount);
                alert('Descuento aplicado correctamente');
            } else {
                alert(response.data.message || 'Código inválido');
                setAppliedDiscount(null);
            }
        } catch (err) {
            console.error('Error validating discount:', err);
            alert('Error al validar descuento');
            setAppliedDiscount(null);
        }
    };

    // =====================================
    // 8. LÓGICA DE CLIENTES
    // =====================================
    const searchCustomers = async (query) => {
        setCustomerSearch(query);
        if (query.length < 3) {
            setCustomers([]);
            return;
        }
        try {
            const response = await api.post('/api/customers/admin/search/', { query }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setCustomers(response.data.data.customers || []);
        } catch (err) {
            console.error('Error searching customers:', err);
        }
    };

    const handleCreateCustomer = async (e) => {
        e.preventDefault();
        try {
            const customerData = {
                ...newCustomer,
                cedula: newCustomer.cedula || null
            };

            const response = await api.post('/api/customers/register/', customerData, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            alert('Cliente creado exitosamente');
            setShowCustomerModal(false);
            setSelectedCustomer(response.data.data.customer);
            setCustomerSearch(`${response.data.data.customer.first_name} ${response.data.data.customer.last_name}`);
            setCustomers([]);
            setNewCustomer({
                email: '',
                password: 'Password123!',
                password_confirmation: 'Password123!',
                first_name: '',
                last_name: '',
                phone: '',
                address: '',
                city: '',
                cedula: ''
            });
        } catch (err) {
            console.error('Error creating customer:', err);
            const errorData = err.response?.data;
            let errorMessage = 'Error al crear cliente';

            if (errorData?.errors) {
                errorMessage += ':\n' + Object.entries(errorData.errors)
                    .map(([key, val]) => `- ${key}: ${val}`)
                    .join('\n');
            } else if (errorData?.message) {
                errorMessage += ': ' + errorData.message;
            } else {
                errorMessage += ': ' + err.message;
            }

            alert(errorMessage);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewCustomer(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // =====================================
    // 9. LÓGICA DE PROCESAMIENTO PRINCIPAL
    // =====================================

    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
            const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
            const isAvailable = product.is_active && product.is_available;

            return matchesCategory && matchesSearch && isAvailable;
        });
    }, [products, selectedCategory, searchTerm]);

    // Validation for split payments
    const isPaymentValid = useMemo(() => {
        if (paymentMethod !== 'dividido') return true;
        const currentExchangeRate = parseFloat(exchangeRate) || 4000;
        
        const getUsdAmount = (curr, amount) => curr === 'USD' ? parseFloat(amount || 0) : parseFloat(amount || 0) / currentExchangeRate;
        
        const split1Usd = getUsdAmount(split1Currency, split1Amount);
        const split2Usd = getUsdAmount(split2Currency, split2Amount);
        
        // Allowed tolerance of 0.02 USD to handle rounding differences
        return (split1Usd + split2Usd) >= (calculateTotal - 0.02);
    }, [paymentMethod, split1Amount, split2Amount, split1Currency, split2Currency, calculateTotal, exchangeRate]);

    const handleInitiatePlaceOrder = (isFeria = false) => {
        if (cart.length === 0) return;
        setPendingIsFeria(isFeria);
        setShowDocTypeModal(true);
    };

    // 🖨️ FUNCIÓN PRINCIPAL CON IMPRESIÓN
    const finalPlaceOrder = async (isFeria = false, chosenDocType = null) => {
        if (cart.length === 0) return;

        const activeDocType = chosenDocType || documentType || 'factura';

        setProcessingOrder(true);
        setShowReviewModal(false);
        setShowDocTypeModal(false);

        let orderType = 'dine_in';
        let tableNumber = selectedTable;
        const DEFAULT_TABLE_NAME = 'GENERICA';

        if (selectedTable === 'takeout') {
            orderType = 'takeout';
            tableNumber = '';
        } else if (!selectedTable || selectedTable === 'Seleccionar mesa...') {
            orderType = 'dine_in';
            tableNumber = DEFAULT_TABLE_NAME;
        } else {
            orderType = 'dine_in';
            tableNumber = selectedTable;
        }

        // Preparar notas con información de pago
        let orderNotes = '';
        if (isEmployeeDiscount) {
            orderNotes += '[VENTA_EMPLEADO]';
        }
        if (currency === 'COP' && paymentMethod !== 'dividido') {
            if (orderNotes) orderNotes += ' | ';
            orderNotes += `[PAGADO_EN_COP] Tasa: ${exchangeRate}`;
        }
        if (cashGiven && paymentMethod !== 'dividido') {
            const displayTotal = currency === 'COP' ? calculateTotal * exchangeRate : calculateTotal;
            const change = cashGiven - displayTotal;
            if (orderNotes) orderNotes += ' | ';
            const currSymbol = currency === 'COP' ? 'COP' : '$';
            orderNotes += `Pago con: ${currSymbol} ${cashGiven} - Cambio: ${currSymbol} ${change}`;
        }

        // Modificado para incluir notas en los items y porcentajes de descuento
        const orderPayload = {
            order_type: orderType,
            table_number: tableNumber,
            notes: orderNotes, // Nueva nota general
            discount_percentage: Number((parseFloat(orderDiscountPercentage) || 0).toFixed(2)),
            payment_method: paymentMethod === 'dividido' ? 'dividido' : (currency === 'COP' ? `${paymentMethod}_cop` : paymentMethod),
            payment_reference: paymentMethod === 'efectivo' || paymentMethod === 'tarjeta' ? '' : transferReference,
            items: cart.map(item => {
                let finalNote = item.note || '';
                if (item.selectedToppings && item.selectedToppings.length > 0) {
                    const toppingsText = `Toppings: ${item.selectedToppings.join(', ')}`;
                    finalNote = finalNote ? `${finalNote} | ${toppingsText}` : toppingsText;
                }
                return {
                    product_id: item.product_id,
                    combo_id: item.combo_id || null,
                    quantity: item.quantity,
                    notes: finalNote, 
                    discount_percentage: Number((parseFloat(item.discount_percentage) || 0).toFixed(2)),
                    selected_variables: item.selectedVariables || []
                };
            }),
            discount_code: appliedDiscount ? appliedDiscount.code : null,
            customer_id: selectedCustomer ? selectedCustomer.id : null,
            skip_print: true, // Evitar que el backend imprima doble
            is_feria: isFeria
        };
        
        if (paymentMethod === 'dividido') {
            const getUsdAmount = (curr, amount) => curr === 'USD' ? parseFloat(amount || 0) : parseFloat(amount || 0) / exchangeRate;
            
            const split1Usd = getUsdAmount(split1Currency, split1Amount);
            const split2Usd = getUsdAmount(split2Currency, split2Amount);

            const totalEnteredUsd = split1Usd + split2Usd;
            
            // Allow a tiny tolerance for floating point math
            if (totalEnteredUsd < calculateTotal - 0.01) {
                alert(`El monto ingresado en el pago dividido ($${totalEnteredUsd.toFixed(2)} USD) es menor al Total Final ($${calculateTotal.toFixed(2)} USD). Por favor, verifica los montos ingresados.`);
                setProcessingOrder(false);
                return;
            }

            let cashUsd = 0, cashCop = 0, transferUsd = 0, transferCop = 0;
            
            const processSplit = (amtStr, cur, meth) => {
                const amt = parseFloat(String(amtStr || '').replace(',', '.')) || 0;
                if (meth === 'efectivo') {
                    if (cur === 'USD') cashUsd += amt;
                    else cashCop += amt;
                } else if (meth === 'transferencia') {
                    if (cur === 'USD') transferUsd += amt;
                    else transferCop += amt;
                } else {
                    if (cur === 'USD') cashUsd += amt;
                    else cashCop += amt;
                }
            };

            processSplit(split1Amount, split1Currency, split1Method);
            processSplit(split2Amount, split2Currency, split2Method);

            orderPayload.notes += `${orderPayload.notes ? ' | ' : ''}[SPLIT] CASH_USD=${cashUsd.toFixed(2)} CASH_COP=${cashCop.toFixed(2)} TRANSFER_USD=${transferUsd.toFixed(2)} TRANSFER_COP=${transferCop.toFixed(2)}`;
            
            const finalCashUsdAmount = cashUsd + (cashCop / exchangeRate);
            const finalTransferUsdAmount = transferUsd + (transferCop / exchangeRate);
            
            orderPayload.cash_amount = Number(finalCashUsdAmount.toFixed(2));
            orderPayload.transfer_amount = Number(finalTransferUsdAmount.toFixed(2));
        } else {
            orderPayload.cash_amount = paymentMethod === 'efectivo' ? Number(calculateTotal.toFixed(2)) : 0;
            orderPayload.transfer_amount = paymentMethod !== 'efectivo' ? Number(calculateTotal.toFixed(2)) : 0;
        }

        try {
            // 1. CREAR LA ORDEN
            const orderResponse = await api.post('/api/orders/orders/', orderPayload, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });

            const createdOrder = orderResponse.data;

            // 2. PREPARAR DATOS PARA EL TICKET (incluyendo notas y correlativo de orden)
            const receiptData = {
                order_number: createdOrder.order_number || createdOrder.id,
                order_sequence: createdOrder.order_sequence,
                customer_name: selectedCustomer
                    ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`.trim()
                    : 'CONSUMIDOR FINAL',
                customer_dni: selectedCustomer ? (selectedCustomer.cedula || selectedCustomer.dni || selectedCustomer.ruc) : '9999999999999',
                cashier_name: currentShift?.user_name || 'CAJA',
                table_number: selectedTable === 'takeout' ? 'PARA LLEVAR' : (selectedTable || 'MESA GENÉRICA'),
                items: cart.map(item => {
                    let receiptNote = item.note || '';
                    if (item.selectedToppings && item.selectedToppings.length > 0) {
                        const toppingsText = `Toppings: ${item.selectedToppings.join(', ')}`;
                        receiptNote = receiptNote ? `${receiptNote} | ${toppingsText}` : toppingsText;
                    }
                    if (item.selectedVariables && item.selectedVariables.length > 0) {
                        const variablesText = item.selectedVariables.map(v => `${v.variable}: ${v.option}`).join(', ');
                        receiptNote = receiptNote ? `${receiptNote} | ${variablesText}` : variablesText;
                    }
                    return {
                        name: item.name,
                        description: item.description || '',
                        quantity: item.quantity,
                        price: parseFloat(item.price),
                        discount_percentage: Number((parseFloat(item.discount_percentage) || 0).toFixed(2)),
                        total: parseFloat((item.price * item.quantity * (1 - (parseFloat(item.discount_percentage) || 0) / 100)).toFixed(2)),
                        note: receiptNote
                    };
                }),
                subtotal: Number(parseFloat(calculateTotal / 1.15).toFixed(2)),
                discount: Number(parseFloat(calculateDiscountAmount).toFixed(2)),
                tax: Number(parseFloat(calculateTotal - (calculateTotal / 1.15)).toFixed(2)), // IVA 15% desglosado
                total: Number(parseFloat(calculateTotal).toFixed(2)),
                payment_method: currency === 'COP' ? `${paymentMethod}_cop` : paymentMethod,
                payment_reference: transferReference,
                document_type: activeDocType, // 'factura' o 'nota_entrega'
                printed_at: new Date().toISOString() // Hora del cliente para el ticket

            };

            if (currency === 'COP') {
                receiptData.total_cop = parseFloat(calculateTotal * exchangeRate);
            }

            // 3. EMISIÓN DE FACTURA ELECTRÓNICA SRI + espera de clave de acceso
            if (createdOrder?.id && activeDocType === 'factura') {
                try {
                    setPrintStep('📤 Emitiendo factura electrónica...');
                    const billingRes = await billingService.emitOrderInvoice(createdOrder.id);
                    console.log('🧾 Respuesta SRI KoaDoc:', billingRes);
                    const invoiceObj = billingRes?.invoice || billingRes?.data?.invoice || billingRes?.data || billingRes;
                    if (invoiceObj) {
                        receiptData.sri_access_key = invoiceObj.access_key || invoiceObj.sri_access_key || invoiceObj.clave_acceso;
                        receiptData.sri_document_number = invoiceObj.number || invoiceObj.document_number || invoiceObj.sri_document_number || invoiceObj.numero_documento;
                    }

                    // Si aún no tenemos la clave, esperar hasta 5 seg consultando la BD
                    if (!receiptData.sri_access_key) {
                        setPrintStep('⏳ Esperando autorización del SRI...');
                        for (let i = 0; i < 5; i++) {
                            await new Promise(r => setTimeout(r, 1000));
                            try {
                                const statusCheck = await billingService.getOrderInvoiceStatus(createdOrder.id);
                                const sd = statusCheck?.data || statusCheck;
                                const key = sd?.access_key || sd?.sri_access_key;
                                if (key) {
                                    receiptData.sri_access_key = key;
                                    receiptData.sri_document_number = sd.document_number || sd.sri_document_number || receiptData.sri_document_number;
                                    console.log(`✅ Clave de acceso obtenida en intento ${i+1}:`, key.slice(0,10) + '...');
                                    break;
                                }
                            } catch (_) {}
                        }
                    }
                } catch (billingErr) {
                    console.warn('⚠️ Nota sobre facturación SRI:', billingErr.response?.data || billingErr);
                }
            } else if (documentType === 'nota_entrega') {
                console.log('📋 Orden procesada como Nota de Entrega local (Sin emisión SRI).');
            }

            // 4. ENVIAR A IMPRIMIR
            setPrintStep('🖨️ Enviando a impresora...');
            try {
                const printResult = await printerService.printReceipt(receiptData);
                console.log('✅ Ticket enviado a impresión:', printResult);
                setPrintStep('');

                // Sin alert(): en Android cancela la apertura de la app KOAPrint (ver CAMBIOS_PANTALLAS.md)
                const orderLabel = createdOrder.order_sequence ? `#${createdOrder.order_sequence}` : (createdOrder.order_number || createdOrder.id);
                const pointsLabel = createdOrder.points_earned > 0 ? ` · +${createdOrder.points_earned} puntos` : '';
                setOrderSuccessNotification(`Orden ${orderLabel} enviada a la impresora${pointsLabel}`);
                setTimeout(() => setOrderSuccessNotification(''), 3500);
            } catch (printError) {
                console.error('⚠️ Error al imprimir:', printError);
                setPrintStep('');
                const backendMsg = printError.response?.data?.error || printError.response?.data?.message || printError.message || 'Error desconocido';
                alert(
                    `⚠️ Orden creada pero ocurrió un detalle al imprimir:\n\n` +
                    `Orden: ${createdOrder.order_number || createdOrder.id}\n` +
                    `Detalle: ${backendMsg}\n\n` +
                    `Verifica que la impresora esté conectada o que la app RawBT esté abierta.`
                );
            }

            // 4. LIMPIAR EL CARRITO Y ESTADOS DE PAGO
            setCart([]);

            setAppliedDiscount(null);
            setDiscountCode('');
            setIsEmployeeDiscount(false);
            setOrderDiscountPercentage(0);
            setSelectedTable('');
            setSelectedCustomer(null);
            setCustomerSearch('');
            setCashGiven(null); // Resetear calculadora
            setInputCash('');
            setCurrency('USD'); // Resetear moneda a USD
            setDocumentType('factura'); // Resetear tipo de documento
            
            // LIMPIEZA ADICIONAL PARA EVITAR ARRASTRE DE DATOS
            setPaymentMethod('efectivo');
            setTransferReference('');
            setSplit1Method('efectivo');
            setSplit1Currency('USD');
            setSplit1Amount('');
            setSplit2Method('transferencia');
            setSplit2Currency('COP');
            setSplit2Amount('');
            setShowReviewModal(false);

        } catch (err) {
            console.error('❌ Error al procesar la orden:', err);
            const errorMsg = err.response?.data
                ? JSON.stringify(err.response.data)
                : 'Error al procesar la orden';
            alert(`❌ Error: ${errorMsg}`);
        } finally {
            setProcessingOrder(false);
        }
    };

    // 🔓 FUNCIÓN PARA ABRIR CAJA MANUALMENTE
    const handleOpenCashDrawer = async () => {
        try {
            await printerService.openCashDrawer();
            alert('✅ Caja abierta');
        } catch (error) {
            alert('❌ Error al abrir caja. Verifica que el agente esté ejecutándose.');
        }
    };

    const openOrderConfirmationModal = () => {
        if (cart.length === 0) {
            alert("El carrito está vacío.");
            return;
        }
        setShowReviewModal(true);
    };

    // =====================================
    // 10. COMPONENTES DE RENDERIZADO
    // =====================================

    const renderReviewDetails = () => (
        <div style={{ padding: screenWidth <= 1366 ? '0.5rem' : '0 1rem' }}>
            {/* Sección de configuración de orden */}
            <div style={{
                backgroundColor: 'var(--sidebar-bg)',
                borderRadius: '8px',
                padding: screenWidth <= 1366 ? '0.75rem' : '1rem',
                marginBottom: '1rem'
            }}>
                {/* Cliente */}
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                        display: 'block',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.5rem'
                    }}>
                        Cliente
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                type="text"
                                placeholder="Buscar por nombre, cédula o teléfono..."
                                style={{
                                    width: '100%',
                                    padding: screenWidth <= 1366 ? '0.5rem' : '0.75rem',
                                    border: '2px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                                    transition: 'all 0.2s',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                value={customerSearch}
                                onChange={(e) => searchCustomers(e.target.value)}
                            />
                            {customers.length > 0 && (
                                <div style={{
                                    position: 'absolute',
                                    zIndex: 10,
                                    width: '100%',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                    marginTop: '0.25rem',
                                    maxHeight: '200px',
                                    overflowY: 'auto'
                                }}>
                                    {customers.map(c => (
                                        <div
                                            key={c.id}
                                            style={{
                                                padding: '0.75rem',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid var(--sidebar-bg)',
                                                transition: 'background-color 0.15s',
                                                minHeight: TOUCH_MIN_SIZE,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                            onClick={() => {
                                                setSelectedCustomer(c);
                                                setCustomerSearch(`${c.first_name} ${c.last_name}`);
                                                setCustomers([]);
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sidebar-bg)'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                                        >
                                            <div>
                                                <p style={{
                                                    fontWeight: '600',
                                                    color: 'var(--primary-color)',
                                                    marginBottom: '0.25rem',
                                                    fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem'
                                                }}>
                                                    {c.first_name} {c.last_name}
                                                </p>
                                                <p style={{
                                                    fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8125rem',
                                                    color: '#6b7280',
                                                    margin: 0
                                                }}>
                                                    {c.email} {c.cedula && `(${c.cedula})`}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            style={{
                                width: TOUCH_MIN_SIZE,
                                height: TOUCH_MIN_SIZE,
                                backgroundColor: 'var(--primary-color)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '1.5rem',
                                fontWeight: '300',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                            onClick={() => setShowCustomerModal(true)}
                            title="Agregar nuevo cliente"
                        >
                            +
                        </button>
                    </div>
                </div>

                {/* Mesa/Tipo de Orden */}
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                        display: 'block',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.5rem'
                    }}>
                        Mesa / Tipo de Orden
                    </label>
                    <select
                        style={{
                            width: '100%',
                            padding: screenWidth <= 1366 ? '0.5rem' : '0.75rem',
                            border: '2px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                            color: 'var(--primary-color)',
                            backgroundColor: '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minHeight: TOUCH_MIN_SIZE
                        }}
                        value={selectedTable}
                        onChange={(e) => setSelectedTable(e.target.value)}
                    >
                        <option value="">Seleccionar mesa...</option>
                        <option value="takeout">Para Llevar (Takeout)</option>
                        {tables.map(table => (
                            <option
                                key={table.id}
                                value={table.number}
                                disabled={table.status !== 'available'}
                            >
                                Mesa {table.number} {table.status !== 'available' ? '(Ocupada)' : ''}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Porcentaje de Descuento (Orden) */}
                <div>
                    <label style={{
                        display: 'block',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.5rem'
                    }}>
                        Descuento a la Orden (%)
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                        {[25, 50, 100].map(pct => (
                            <button
                                key={pct}
                                style={{
                                    flex: 1,
                                    padding: '0.5rem',
                                    backgroundColor: orderDiscountPercentage === pct ? 'var(--primary-color)' : '#f3f4f6',
                                    border: `1px solid ${orderDiscountPercentage === pct ? 'var(--primary-color)' : '#d1d5db'}`,
                                    borderRadius: '8px',
                                    color: orderDiscountPercentage === pct ? '#ffffff' : '#374151',
                                    fontWeight: '600',
                                    fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                                    cursor: 'pointer',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={() => setOrderDiscountPercentage(pct)}
                            >
                                {pct}%
                            </button>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            placeholder="Otro porcentaje"
                            style={{
                                flex: 1,
                                padding: screenWidth <= 1366 ? '0.5rem' : '0.75rem',
                                border: '2px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                                transition: 'all 0.2s',
                                minHeight: TOUCH_MIN_SIZE
                            }}
                            value={orderDiscountPercentage || ''}
                            onChange={(e) => setOrderDiscountPercentage(Math.max(0, Math.min(100, e.target.value)))}
                        />
                        <button
                            style={{
                                padding: screenWidth <= 1366 ? '0 0.75rem' : '0 1.5rem',
                                backgroundColor: '#fee2e2',
                                border: 'none',
                                borderRadius: '8px',
                                color: '#dc2626',
                                fontWeight: '600',
                                fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                minHeight: TOUCH_MIN_SIZE,
                                minWidth: TOUCH_MIN_SIZE
                            }}
                            onClick={() => {
                                setOrderDiscountPercentage(0);
                                setIsEmployeeDiscount(false);
                            }}
                        >
                            Limpiar
                        </button>
                        <button
                            style={{
                                padding: screenWidth <= 1366 ? '0 0.5rem' : '0 1rem',
                                backgroundColor: isEmployeeDiscount ? 'var(--primary-color)' : '#f3f4f6',
                                border: '2px solid #d1d5db',
                                borderRadius: '8px',
                                color: isEmployeeDiscount ? '#ffffff' : '#374151',
                                fontWeight: '600',
                                fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: TOUCH_MIN_SIZE,
                                marginLeft: '0.5rem'
                            }}
                            onClick={() => {
                                const newValue = !isEmployeeDiscount;
                                setIsEmployeeDiscount(newValue);
                                setOrderDiscountPercentage(newValue ? 15 : 0);
                            }}
                            title="Descuento Empleado (15%)"
                        >
                            <i className="bi bi-person-badge" style={{ marginRight: '0.25rem' }}></i> Empleado
                        </button>
                    </div>
                </div>
            </div>

            {/* Resumen de productos */}
            <div style={{ marginBottom: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px' }}>
                <p style={{ fontSize: screenWidth <= 1366 ? '0.875rem' : '1rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                    Cliente: {selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : 'CONSUMIDOR FINAL'}
                </p>
                <p style={{ fontSize: screenWidth <= 1366 ? '0.75rem' : '0.9rem', color: '#4b5563' }}>
                    Mesa/Tipo: {selectedTable === 'takeout' ? 'Para Llevar' : selectedTable || 'Mesa Genérica (DINE-IN)'}
                </p>
            </div>

            <div style={{
                maxHeight: screenWidth <= 1366 ? '40vh' : '30vh',
                overflowY: 'auto',
                marginBottom: '1rem',
                fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9rem'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: 'var(--sidebar-bg)' }}>
                        <tr>
                            <th style={{
                                textAlign: 'left',
                                padding: screenWidth <= 1366 ? '0.25rem' : '0.5rem',
                                fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8rem',
                                color: '#4b5563'
                            }}>PRODUCTO</th>
                            <th style={{
                                width: '15%',
                                textAlign: 'right',
                                padding: screenWidth <= 1366 ? '0.25rem' : '0.5rem',
                                fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8rem',
                                color: '#4b5563'
                            }}>CANT.</th>
                            <th style={{
                                width: '25%',
                                textAlign: 'right',
                                padding: screenWidth <= 1366 ? '0.25rem' : '0.5rem',
                                fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8rem',
                                color: '#4b5563'
                            }}>TOTAL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cart.map((item, index) => (
                            <React.Fragment key={index}>
                                <tr>
                                    <td style={{
                                        padding: screenWidth <= 1366 ? '0.25rem 0' : '0.5rem 0',
                                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9rem'
                                    }}>
                                        <div>
                                            {item.name}
                                            {item.note && (
                                                <div style={{
                                                    fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8rem',
                                                    color: '#6b7280',
                                                    fontStyle: 'italic',
                                                    marginTop: '2px'
                                                }}>
                                                    ({item.note})
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{
                                        textAlign: 'right',
                                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9rem'
                                    }}>{item.quantity}</td>
                                    <td style={{
                                        textAlign: 'right',
                                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9rem',
                                        fontWeight: '600'
                                    }}>
                                        {item.discount_percentage > 0 && (
                                            <div style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                                {formatCurrency(item.price * item.quantity)}
                                            </div>
                                        )}
                                        <div style={{ color: item.discount_percentage > 0 ? '#10b981' : 'inherit' }}>
                                            {formatCurrency(item.price * item.quantity * (1 - (parseFloat(item.discount_percentage) || 0) / 100))}
                                        </div>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* SECCIÓN: MONEDA DE PAGO */}
            <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: 'var(--sidebar-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
            }}>
                <h4 style={{
                    margin: '0 0 0.5rem 0',
                    color: 'var(--primary-color)',
                    fontSize: screenWidth <= 1366 ? '0.9rem' : '1rem'
                }}>
                    Moneda de Pago
                </h4>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <button
                        onClick={() => { setCurrency('USD'); setCashGiven(null); setInputCash(''); }}
                        style={{
                            flex: 1, padding: '0.75rem', borderRadius: '8px',
                            backgroundColor: currency === 'USD' ? '#0d47a1' : '#ffffff',
                            color: currency === 'USD' ? '#ffffff' : '#4b5563',
                            border: `2px solid ${currency === 'USD' ? '#0d47a1' : '#d1d5db'}`,
                            fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s'
                        }}
                    >
                        🇺🇸 Dólares (USD)
                    </button>
                    <button
                        onClick={() => { setCurrency('COP'); setCashGiven(null); setInputCash(''); }}
                        style={{
                            flex: 1, padding: '0.75rem', borderRadius: '8px',
                            backgroundColor: currency === 'COP' ? '#1b5e20' : '#ffffff',
                            color: currency === 'COP' ? '#ffffff' : '#4b5563',
                            border: `2px solid ${currency === 'COP' ? '#1b5e20' : '#d1d5db'}`,
                            fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s'
                        }}
                    >
                        🇨🇴 Pesos (COP)
                    </button>
                </div>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '8px', border: '1px solid #d1d5db', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: '#4b5563', fontWeight: 'bold' }}>Tasa de Cambio Actual:</span>
                    <span style={{ fontSize: '1rem', color: 'var(--primary-color)', fontWeight: 'bold' }}>$ {exchangeRate.toLocaleString('es-CO')} COP</span>
                </div>

            </div>

            {/* SECCIÓN: MÉTODO DE PAGO */}
            <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: 'var(--sidebar-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
            }}>
                <h4 style={{
                    margin: '0 0 0.5rem 0',
                    color: 'var(--primary-color)',
                    fontSize: screenWidth <= 1366 ? '0.9rem' : '1rem'
                }}>
                    Método de Pago
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {[
                            { 
                                id: 'efectivo', 
                                icon: (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 256 256">
                                        <path d="M240,88V184a16,16,0,0,1-16,16H32a16,16,0,0,1-16-16V88A16,16,0,0,1,32,72H224A16,16,0,0,1,240,88Zm-16,0H32V184H224ZM128,104a24,24,0,1,0,24,24A24,24,0,0,0,128,104Zm0,32a8,8,0,1,1,8-8A8,8,0,0,1,128,136Zm88-32a8,8,0,0,0-8-8H192a8,8,0,0,0,0,16h16A8,8,0,0,0,216,104Zm-16,64H192a8,8,0,0,0,0,16h8a8,8,0,0,0,0-16ZM64,104a8,8,0,0,0,8-8H56a8,8,0,0,0,0,16h8A8,8,0,0,0,64,104Zm0,64a8,8,0,0,0-8,8h16a8,8,0,0,0,0-16H56A8,8,0,0,0,64,168Z"></path>
                                    </svg>
                                )
                            },
                            { 
                                id: 'transferencia', 
                                icon: (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 256 256">
                                        <path d="M240,208H224V104a8,8,0,0,0-4-6.93l-88-51a8,8,0,0,0-8,0l-88,51A8,8,0,0,0,32,104V208H16a8,8,0,0,0,0,16H240a8,8,0,0,0,0-16ZM48,108.62l80-46.4,80,46.4V208H176V120a8,8,0,0,0-16,0v88H96V120a8,8,0,0,0-16,0v88H48ZM128,144a16,16,0,1,0-16-16A16,16,0,0,0,128,144Z"></path>
                                    </svg>
                                )
                            },
                            {
                                id: 'dividido',
                                icon: (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 256 256">
                                        <path d="M216,48H40A16,16,0,0,0,24,64V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V64A16,16,0,0,0,216,48ZM40,64H112V192H40ZM216,192H128V64h88V192Z"></path>
                                    </svg>
                                )
                            }
                        ].map(method => (
                            <button
                                key={method.id}
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '1rem',
                                    backgroundColor: paymentMethod === method.id ? 'var(--primary-color)' : '#ffffff',
                                    border: `2px solid ${paymentMethod === method.id ? 'var(--primary-color)' : '#d1d5db'}`,
                                    borderRadius: '12px',
                                    color: paymentMethod === method.id ? '#ffffff' : '#4b5563',
                                    cursor: 'pointer',
                                    minHeight: '70px',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                onClick={() => setPaymentMethod(method.id)}
                            >
                                {method.icon}
                            </button>
                        ))}
                    </div>

                    {paymentMethod === 'transferencia' && (
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                                Número de Referencia:
                            </label>
                            <input
                                type="text"
                                placeholder="Ej: 12345678"
                                value={transferReference}
                                onChange={(e) => setTransferReference(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    outline: 'none',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                            />
                        </div>
                    )}
                    {paymentMethod === 'dividido' && (
                        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                            <h5 style={{ margin: '0 0 0.5rem 0', color: '#4b5563' }}>Pago 1</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.2fr', gap: '0.5rem', marginBottom: '1rem' }}>
                                <select value={split1Method} onChange={(e) => setSplit1Method(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                                    <option value="efectivo">Efectivo</option>
                                    <option value="transferencia">Transferencia</option>
                                </select>
                                <select value={split1Currency} onChange={(e) => setSplit1Currency(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                                    <option value="USD">USD</option>
                                    <option value="COP">COP</option>
                                </select>
                                <input type="number" value={split1Amount} onChange={(e) => setSplit1Amount(e.target.value)} placeholder="Monto" style={{ width: '100%', minWidth: '0', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                            </div>

                            <h5 style={{ margin: '0 0 0.5rem 0', color: '#4b5563' }}>Pago 2</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.2fr', gap: '0.5rem' }}>
                                <select value={split2Method} onChange={(e) => setSplit2Method(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                                    <option value="efectivo">Efectivo</option>
                                    <option value="transferencia">Transferencia</option>
                                </select>
                                <select value={split2Currency} onChange={(e) => setSplit2Currency(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                                    <option value="USD">USD</option>
                                    <option value="COP">COP</option>
                                </select>
                                <input type="number" value={split2Amount} onChange={(e) => setSplit2Amount(e.target.value)} placeholder="Monto" style={{ width: '100%', minWidth: '0', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                            </div>
                            
                            {(() => {
                                const val1Str = String(split1Amount).trim();
                                if (!val1Str || isNaN(parseFloat(val1Str))) return null;
                                
                                const currentRate = parseFloat(exchangeRate) || 4000;
                                const getUsdAmount = (curr, amount) => curr === 'USD' ? parseFloat(amount || 0) : parseFloat(amount || 0) / currentRate;
                                const val1Usd = getUsdAmount(split1Currency, split1Amount);
                                
                                const remainUsd = calculateTotal - val1Usd;
                                
                                if (remainUsd <= 0.01) {
                                    return <div style={{marginTop: '0.75rem', color: '#10b981', fontWeight: 'bold', fontSize: '0.875rem', textAlign: 'center'}}>Pago completado</div>;
                                }
                                
                                const remainCOP = remainUsd * currentRate;
                                
                                return (
                                    <div style={{marginTop: '1rem', padding: '0.75rem', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe'}}>
                                        <div style={{fontSize: '0.875rem', color: '#1e3a8a', fontWeight: 'bold', marginBottom: '0.25rem'}}>Falta por pagar para el Pago 2:</div>
                                        <div style={{display: 'flex', gap: '1rem', fontSize: '0.9rem'}}>
                                            <span style={{
                                                color: split2Currency === 'USD' ? '#1d4ed8' : '#9ca3af', 
                                                fontWeight: split2Currency === 'USD' ? 'bold' : 'normal'
                                            }}>
                                                ${remainUsd.toFixed(2)} USD
                                            </span>
                                            <span style={{
                                                color: split2Currency === 'COP' ? '#1d4ed8' : '#9ca3af', 
                                                fontWeight: split2Currency === 'COP' ? 'bold' : 'normal'
                                            }}>
                                                / COP {remainCOP.toLocaleString('es-CO', {maximumFractionDigits: 2})}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>

            {/* SECCIÓN: CALCULADORA DE VUELTO */}
            <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: 'var(--sidebar-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
            }}>
                <h4 style={{
                    margin: '0 0 0.5rem 0',
                    color: 'var(--primary-color)',
                    fontSize: screenWidth <= 1366 ? '0.9rem' : '1rem'
                }}>
                    Calculadora de Vuelto {currency === 'COP' ? '(COP)' : '(USD)'}
                </h4>

                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#374151' }}>
                        Ingreso Manual:
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                            type="number"
                            value={inputCash}
                            onChange={(e) => {
                                const val = e.target.value;
                                setInputCash(val);
                                setCashGiven(val ? parseFloat(val) : null);
                            }}
                            placeholder="0.00"
                            style={{
                                flex: 1,
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                fontSize: '1rem'
                            }}
                        />
                        <button
                            onClick={() => {
                                setCashGiven(null);
                                setInputCash('');
                            }}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#fee2e2',
                                color: '#b91c1c',
                                border: '1px solid #ef4444',
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            Borrar
                        </button>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
                    {(currency === 'COP' ? [2000, 5000, 10000, 20000, 50000, 100000] : [1, 2, 5, 10, 20, 50, 100]).map(bill => (
                        <button
                            key={bill}
                            onClick={() => {
                                const newVal = (cashGiven || 0) + bill;
                                setCashGiven(newVal);
                                setInputCash(newVal.toString());
                            }}
                            style={{
                                padding: '0.5rem',
                                backgroundColor: '#ffffff',
                                color: 'var(--primary-color)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                minHeight: TOUCH_MIN_SIZE
                            }}
                        >
                            + {currency === 'COP' ? 'COP ' : '$'}{bill}
                        </button>
                    ))}
                </div>

                {cashGiven !== null && (
                    <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#ffffff',
                        borderRadius: '6px',
                        border: '1px solid var(--sidebar-bg)',
                        textAlign: 'center'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                            <span style={{ color: '#6b7280' }}>Total a Pagar:</span>
                            <span style={{ fontWeight: 'bold' }}>
                                {currency === 'COP' 
                                    ? `COP ${(calculateTotal * exchangeRate).toLocaleString('es-CO')}` 
                                    : formatCurrency(calculateTotal)}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                            <span style={{ color: '#6b7280' }}>Efectivo Recibido:</span>
                            <span style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                {currency === 'COP' 
                                    ? `COP ${cashGiven.toLocaleString('es-CO')}` 
                                    : formatCurrency(cashGiven)}
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: '0.5rem',
                            paddingTop: '0.5rem',
                            borderTop: '1px dashed var(--border-color)',
                            fontSize: '1.2rem',
                            fontWeight: '800'
                        }}>
                            <span style={{ color: 'var(--primary-color)' }}>VUELTO:</span>
                            <span style={{ color: (cashGiven - (currency === 'COP' ? calculateTotal * exchangeRate : calculateTotal)) < 0 ? '#ef4444' : 'var(--primary-color)' }}>
                                {currency === 'COP' 
                                    ? `COP ${(cashGiven - (calculateTotal * exchangeRate)).toLocaleString('es-CO')}`
                                    : formatCurrency(cashGiven - calculateTotal)}
                            </span>
                        </div>
                        {(cashGiven - (currency === 'COP' ? calculateTotal * exchangeRate : calculateTotal)) < 0 && (
                            <p style={{ color: '#ef4444', fontSize: '0.8rem', margin: '0.5rem 0 0 0' }}>
                                ⚠️ Monto insuficiente
                            </p>
                        )}
                    </div>
                )}
            </div>

            <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: '1rem', marginTop: '1rem' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: screenWidth <= 1366 ? '0.875rem' : '1rem',
                    color: '#6b7280'
                }}>
                    <span>Subtotal</span>
                    <span>{currency === 'COP' ? `COP ${(calculateSubtotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateSubtotal)}</span>
                </div>

                {appliedDiscount && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem',
                        color: '#dc2626',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '1rem'
                    }}>
                        <span>Descuento</span>
                        <span>- {formatCurrency(calculateDiscountAmount)}</span>
                    </div>
                )}

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: screenWidth <= 1366 ? '1.25rem' : '1.5rem',
                    fontWeight: 'bold',
                    borderTop: '1px solid #ccc',
                    paddingTop: '0.75rem'
                }}>
                    <span>Total Final</span>
                    <span style={{ color: 'var(--primary-color)' }}>{currency === 'COP' ? `COP ${(calculateTotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateTotal)}</span>
                </div>
            </div>
        </div>
    );

    // Modal para agregar nota
    const renderNoteModal = () => (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: screenWidth <= 1366 ? '0.5rem' : '1rem'
        }}>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                width: '100%',
                maxWidth: screenWidth <= 1366 ? '95%' : '500px',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                padding: screenWidth <= 1366 ? '1rem' : '1.5rem'
            }}>
                <div style={{
                    marginBottom: '1rem',
                    borderBottom: '2px solid #e5e7eb',
                    paddingBottom: '0.75rem'
                }}>
                    <h3 style={{
                        fontSize: screenWidth <= 1366 ? '1.125rem' : '1.25rem',
                        fontWeight: '700',
                        color: '#111827',
                        margin: 0
                    }}>
                        Agregar Nota Especial
                    </h3>
                    <p style={{
                        fontSize: screenWidth <= 1366 ? '0.75rem' : '0.875rem',
                        color: '#6b7280',
                        marginTop: '0.25rem'
                    }}>
                        Escribe las especificaciones del producto (aparecerá entre paréntesis en el ticket)
                    </p>
                </div>

                <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Ej: Sin cebolla, extra queso, bien cocido, etc."
                    style={{
                        width: '100%',
                        height: '120px',
                        padding: '0.75rem',
                        border: '2px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '1rem',
                        fontFamily: 'inherit',
                        resize: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '1.5rem'
                    }}
                    maxLength={100}
                />

                <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        style={{
                            padding: screenWidth <= 1366 ? '0.5rem 1rem' : '0.75rem 1.5rem',
                            backgroundColor: '#ffffff',
                            border: '2px solid #d1d5db',
                            borderRadius: '8px',
                            color: '#374151',
                            fontWeight: '600',
                            fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minHeight: TOUCH_MIN_SIZE
                        }}
                        onClick={cancelNote}
                    >
                        Cancelar
                    </button>
                    <button
                        style={{
                            padding: screenWidth <= 1366 ? '0.5rem 1rem' : '0.75rem 1.5rem',
                            backgroundColor: 'var(--primary-color)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontWeight: '600',
                            fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minHeight: TOUCH_MIN_SIZE
                        }}
                        onClick={saveNote}
                    >
                        Guardar Nota
                    </button>
                </div>
            </div>
        </div>
    );

    // ====== OVERLAY DE CARGA ANIMADO (Muestra el paso actual durante la venta) ======
    const renderPrintOverlay = () => {
        if (!printStep) return null;
        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.65)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9999
            }}>
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    padding: '2.5rem 2rem',
                    textAlign: 'center',
                    maxWidth: '340px',
                    width: '90%',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
                }}>
                    {/* Spinner */}
                    <div style={{
                        width: '56px', height: '56px', margin: '0 auto 1.25rem',
                        border: '5px solid #e5e7eb',
                        borderTop: '5px solid var(--primary-color, #6366f1)',
                        borderRadius: '50%',
                        animation: 'spin 0.9s linear infinite'
                    }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    <p style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                        {printStep}
                    </p>
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
                        Por favor espera, no cierres la pantalla...
                    </p>
                </div>
            </div>
        );
    };

    // Renderizar vista con botones abajo (para pantallas <= 1366px - menos de 16 pulgadas)
    const renderCompactView = () => (
        <div style={{
            height: '100dvh', // Usar dvh para móviles/tablets
            maxHeight: '-webkit-fill-available', // Fallback iOS
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--sidebar-bg)',
            overflow: 'hidden',
            position: 'fixed', // Fijar viewport
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: '#ffffff',
                borderBottom: '2px solid #e5e7eb',
                padding: '0.75rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                flexShrink: 0,
                zIndex: 10
            }}>
                <h1 style={{
                    fontSize: screenWidth <= 768 ? '1.25rem' : '1.5rem',
                    fontWeight: '700',
                    color: '#111827',
                    margin: 0,
                    textAlign: 'center'
                }}>
                    Punto de Venta
                </h1>
            </div>

            {/* Contenido principal - Alterna entre productos y orden */}
            {!showOrderDetails ? (
                // Vista de productos
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0, // Clave para que el scroll interno funcione en flex
                    overflow: 'hidden'
                }}>
                    {/* Filtros */}
                    <div style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0
                    }}>
                        <div style={{
                            display: 'flex',
                            overflowX: 'auto',
                            gap: '0.5rem',
                            paddingBottom: '0.25rem'
                        }}>
                            <button
                                style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '6px',
                                    border: selectedCategory === 'all' ? 'none' : '2px solid #d1d5db',
                                    backgroundColor: selectedCategory === 'all' ? 'var(--primary-color)' : '#ffffff',
                                    color: selectedCategory === 'all' ? '#ffffff' : '#374151',
                                    fontWeight: '600',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={() => setSelectedCategory('all')}
                            >
                                Todos
                            </button>
                            {promotions.length > 0 && (
                                <button
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === 'promotions' ? 'none' : '2px solid #fbbf24',
                                        backgroundColor: selectedCategory === 'promotions' ? '#f59e0b' : '#fffbeb',
                                        color: selectedCategory === 'promotions' ? '#ffffff' : '#b45309',
                                        fontWeight: '700',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory('promotions')}
                                >
                                    ⭐ Promociones
                                </button>
                            )}
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === cat.id ? 'none' : '2px solid #d1d5db',
                                        backgroundColor: selectedCategory === cat.id ? 'var(--primary-color)' : '#ffffff',
                                        color: selectedCategory === cat.id ? '#ffffff' : '#374151',
                                        fontWeight: '600',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory(cat.id)}
                                >
                                    {cat.name.length > 12 ? cat.name.substring(0, 10) + '...' : cat.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Grid Productos */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.75rem',
                        backgroundColor: 'var(--sidebar-bg)'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: screenWidth <= 768 ?
                                'repeat(auto-fill, minmax(140px, 1fr))' :
                                'repeat(auto-fill, minmax(160px, 1fr))',
                            gap: '0.75rem'
                        }}>
                            {(selectedCategory === 'promotions' ? promotions : filteredProducts).map(item => (
                                <div
                                    key={item.id}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                    }}
                                    onClick={() => selectedCategory === 'promotions' ? handleAddPromo(item) : handleProductClick(item)}
                                    onMouseEnter={screenWidth > 768 ? (e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                        e.currentTarget.style.borderColor = selectedCategory === 'promotions' ? '#f59e0b' : 'var(--primary-color)';
                                    } : undefined}
                                    onMouseLeave={screenWidth > 768 ? (e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                    } : undefined}
                                >
                                    <div style={{
                                        height: screenWidth <= 768 ? '80px' : '100px',
                                        backgroundColor: 'var(--sidebar-bg)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.5rem'
                                    }}>
                                        {item.image ? (
                                            <img
                                                src={item.image.startsWith('http') ? item.image : `${process.env.REACT_APP_FAST_FOOD_SERVICE}${item.image}`}
                                                alt={item.name}
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        ) : (
                                            <span style={{
                                                color: '#94a3b8',
                                                fontSize: '0.75rem',
                                                textAlign: 'center'
                                            }}>
                                                Sin imagen
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ padding: '0.5rem' }}>
                                        <h3 style={{
                                            fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                            fontWeight: '600',
                                            color: selectedCategory === 'promotions' ? '#b45309' : 'var(--primary-color)',
                                            marginBottom: '0.25rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {item.name}
                                        </h3>
                                        {item.description && (
                                            <p style={{
                                                fontSize: '0.75rem',
                                                color: '#6b7280',
                                                marginBottom: '0.25rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}>
                                                {item.description}
                                            </p>
                                        )}
                                        <p style={{
                                            fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                                            fontWeight: '700',
                                            color: selectedCategory === 'promotions' ? '#b45309' : 'var(--primary-color)',
                                            margin: 0
                                        }}>
                                            ${item.price}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                // Vista de orden
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                    {/* Header de orden */}
                    <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0,
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <h3 style={{
                            fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                            fontWeight: '700',
                            color: '#111827',
                            margin: 0
                        }}>
                            Orden Actual ({cart.length})
                        </h3>
                        <button
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: 'var(--primary-color)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                minHeight: TOUCH_MIN_SIZE
                            }}
                            onClick={() => setShowOrderDetails(false)}
                        >
                            ← Productos
                        </button>
                    </div>

                    {/* Contenido del carrito */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.75rem',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {cart.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '3rem 1rem',
                                color: '#9ca3af',
                                fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem'
                            }}>
                                <p style={{ margin: 0 }}>No hay productos en el carrito</p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                flex: 1
                            }}>
                                {cart.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            padding: '0.75rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem'
                                        }}
                                    >
                                        {/* Información del producto */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{
                                                    fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                                    fontWeight: '600',
                                                    color: 'var(--primary-color)',
                                                    marginBottom: '0.25rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {item.name}
                                                </h4>
                                                <p style={{
                                                    fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                    color: '#6b7280',
                                                    margin: 0
                                                }}>
                                                    {item.discount_percentage > 0 ? (
                                                        <>
                                                            <span style={{ textDecoration: 'line-through', marginRight: '0.25rem', color: '#9ca3af' }}>
                                                                {formatCurrency(item.price)}
                                                            </span>
                                                            <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                                                                {formatCurrency(item.price * (1 - (parseFloat(item.discount_percentage) || 0) / 100))} c/u
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>{formatCurrency(item.price)} c/u</>
                                                    )}
                                                </p>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <button
                                                        style={{
                                                            width: screenWidth <= 768 ? '36px' : '40px',
                                                            height: screenWidth <= 768 ? '36px' : '40px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#6b7280',
                                                            fontSize: screenWidth <= 768 ? '1rem' : '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, -1)}
                                                    >
                                                        −
                                                    </button>
                                                    <span style={{
                                                        width: screenWidth <= 768 ? '30px' : '34px',
                                                        textAlign: 'center',
                                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                                        fontWeight: '600',
                                                        color: 'var(--primary-color)'
                                                    }}>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        style={{
                                                            width: screenWidth <= 768 ? '36px' : '40px',
                                                            height: screenWidth <= 768 ? '36px' : '40px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#6b7280',
                                                            fontSize: screenWidth <= 768 ? '1rem' : '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, 1)}
                                                    >
                                                        +
                                                    </button>
                                                </div>

                                                <button
                                                    style={{
                                                        width: screenWidth <= 768 ? '36px' : '40px',
                                                        height: screenWidth <= 768 ? '36px' : '40px',
                                                        backgroundColor: '#fee2e2',
                                                        border: '2px solid #fecaca',
                                                        borderRadius: '6px',
                                                        color: '#dc2626',
                                                        fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    onClick={() => removeFromCart(item)}
                                                    title="Eliminar producto"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>

                                        {/* Nota y descuento del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingTop: '0.5rem',
                                            borderTop: '1px dashed #e5e7eb'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                {item.note ? (
                                                    <div style={{
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        color: '#6b7280',
                                                        fontStyle: 'italic',
                                                        backgroundColor: 'var(--sidebar-bg)',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        <strong>Nota:</strong> {item.note}
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        color: '#9ca3af',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Sin notas (mt: {item.max_toppings})
                                                    </span>
                                                )}
                                            </div>

                                            {/* Variables seleccionadas */}
                                            {item.selectedVariables && item.selectedVariables.length > 0 && (
                                                <div style={{ padding: '0.25rem 0', width: '100%' }}>
                                                    {item.selectedVariables.map((v, idx) => (
                                                        <div key={idx} style={{
                                                            fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                            color: '#4b5563',
                                                            backgroundColor: '#f3f4f6',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            display: 'inline-block',
                                                            marginRight: '4px',
                                                            marginBottom: '4px'
                                                        }}>
                                                            <strong>{v.variable}:</strong> {v.option}
                                                            {v.extra_price > 0 && ` (+$${v.extra_price.toFixed(2)})`}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <span style={{ padding: '0.25rem', backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem' }}>%</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={item.discount_percentage || ''}
                                                        onChange={(e) => updateItemDiscount(item, e.target.value)}
                                                        placeholder="0"
                                                        style={{
                                                            width: '32px',
                                                            border: 'none',
                                                            padding: '0.25rem',
                                                            textAlign: 'center',
                                                            fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                </div>

                                                <button
                                                    style={{
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: item.note ? '#fef3c7' : 'var(--sidebar-bg)',
                                                        border: `1px solid ${item.note ? '#fbbf24' : '#d1d5db'}`,
                                                        borderRadius: '4px',
                                                        color: item.note ? '#92400e' : '#374151',
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        marginLeft: '0.25rem',
                                                        whiteSpace: 'nowrap',
                                                        minHeight: TOUCH_MIN_SIZE
                                                    }}
                                                    onClick={() => handleAddNote(item)}
                                                    title={item.note ? "Editar nota" : "Agregar nota"}
                                                >
                                                    {item.note ? '📝 Editar' : '✏️ Nota'}
                                                </button>
                                                
                                                    <button
                                                        onClick={() => {
                                                            setActiveToppingsItemIndex(index);
                                                            setShowToppingsModal(true);
                                                        }}
                                                        style={{
                                                            padding: '0.25rem 0.5rem',
                                                            backgroundColor: item.selectedToppings?.length > 0 ? 'var(--primary-color)' : 'var(--sidebar-bg)',
                                                            border: `1px solid ${item.selectedToppings?.length > 0 ? 'var(--primary-color)' : '#d1d5db'}`,
                                                            borderRadius: '4px',
                                                            color: item.selectedToppings?.length > 0 ? '#fff' : '#374151',
                                                            fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                            fontWeight: '500',
                                                            cursor: 'pointer',
                                                            marginLeft: '0.25rem',
                                                            whiteSpace: 'nowrap',
                                                            minHeight: TOUCH_MIN_SIZE
                                                        }}
                                                        title="Agregar toppings"
                                                    >
                                                        ➕ Toppings {item.selectedToppings?.length > 0 ? `(${item.selectedToppings.length}/${item.max_toppings})` : ''}
                                                    </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Totales y Botones */}
                        {cart.length > 0 && (
                            <div style={{
                                marginTop: 'auto',
                                paddingTop: '1rem',
                                borderTop: '2px solid #e5e7eb'
                            }}>
                                {/* Totales */}
                                <div style={{
                                    paddingBottom: '1rem',
                                    marginBottom: '0.75rem'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.5rem',
                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                        color: '#6b7280'
                                    }}>
                                        <span>Subtotal</span>
                                        <span style={{ fontWeight: '600' }}>{currency === 'COP' ? `COP ${(calculateSubtotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateSubtotal)}</span>
                                    </div>

                                    {appliedDiscount && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '0.5rem',
                                            fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                            color: '#dc2626',
                                            fontWeight: '600'
                                        }}>
                                            <span>Descuento</span>
                                            <span>- {formatCurrency(calculateDiscountAmount)}</span>
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        paddingTop: '0.75rem',
                                        borderTop: '2px solid #e5e7eb',
                                        fontSize: screenWidth <= 768 ? '1.25rem' : '1.5rem',
                                        fontWeight: '700',
                                        color: '#111827'
                                    }}>
                                        <span>Total</span>
                                        <span style={{ color: 'var(--primary-color)' }}>{currency === 'COP' ? `COP ${(calculateTotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateTotal)}</span>
                                    </div>
                                </div>

                                {/* Botones principales */}
                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                    <button
                                        style={{
                                            flex: 1,
                                            padding: '0.75rem',
                                            backgroundColor: '#f59e0b',
                                            border: 'none',
                                            borderRadius: '8px',
                                            color: '#ffffff',
                                            fontSize: screenWidth <= 768 ? '0.875rem' : '0.95rem',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            minHeight: TOUCH_MIN_SIZE,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '4px'
                                        }}
                                        onClick={handleHoldCurrentOrder}
                                        title="Poner orden actual en espera"
                                    >
                                        ⏸️ En Espera
                                    </button>

                                    <button
                                        style={{
                                            flex: 2,
                                            padding: '0.75rem',
                                            backgroundColor: processingOrder ? '#d1d5db' : 'var(--primary-color)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            color: '#ffffff',
                                            fontSize: screenWidth <= 768 ? '0.95rem' : '1.05rem',
                                            fontWeight: '700',
                                            cursor: processingOrder ? 'not-allowed' : 'pointer',
                                            minHeight: TOUCH_MIN_SIZE
                                        }}
                                        onClick={openOrderConfirmationModal}
                                        disabled={processingOrder}
                                    >
                                        {processingOrder ? 'Procesando...' : 'Revisar y Pagar'}
                                    </button>
                                </div>

                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={handleOpenCashDrawer}
                                >
                                    🔓 Abrir Caja
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Barra inferior con botones */}
            <div style={{
                backgroundColor: '#ffffff',
                borderTop: '2px solid #e5e7eb',
                padding: '0.5rem',
                display: 'flex',
                gap: '0.5rem',
                flexShrink: 0
            }}>
                <button
                    style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: showOrderDetails ? '#e5e7eb' : 'var(--primary-color)',
                        border: 'none',
                        borderRadius: '8px',
                        color: showOrderDetails ? '#374151' : '#ffffff',
                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minHeight: TOUCH_MIN_SIZE
                    }}
                    onClick={() => setShowOrderDetails(false)}
                >
                    Productos
                </button>
                <button
                    style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: !showOrderDetails ? '#e5e7eb' : 'var(--primary-color)',
                        border: 'none',
                        borderRadius: '8px',
                        color: !showOrderDetails ? '#374151' : '#ffffff',
                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minHeight: TOUCH_MIN_SIZE,
                        position: 'relative'
                    }}
                    onClick={() => setShowOrderDetails(true)}
                >
                    Orden {cart.length > 0 && (
                        <span style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {cart.length}
                        </span>
                    )}
                </button>
            </div>
        </div>
    );

    // Renderizar vista de escritorio dividida (para pantallas > 1366px - más de 16 pulgadas)
    const renderDesktopView = () => (
        <div ref={posViewRef} style={{
            height: `calc(100dvh - ${posViewTop}px)`,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--sidebar-bg)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: '#ffffff',
                borderBottom: '2px solid #e5e7eb',
                padding: '1rem 1.5rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div>
                    <h1 style={{
                        fontSize: '1.75rem',
                        fontWeight: '700',
                        color: '#111827',
                        margin: 0,
                        letterSpacing: '-0.025em'
                    }}>
                        Punto de Venta
                    </h1>
                </div>

                {/* Pestaña / Botón de Órdenes en Espera y Configuración de Secuencia */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <button
                        type="button"
                        onClick={() => setShowRegalosModal(true)}
                        style={{
                            padding: '0.6rem 1.1rem',
                            backgroundColor: 'var(--secondary-color)',
                            color: 'var(--primary-color)',
                            border: '2px solid var(--primary-color)',
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}
                        title="Canjear regalos con puntos de fidelidad"
                    >
                        <Regalo size={18} />
                        <span>Regalos</span>
                    </button>
                    {isAdmin && (
                        <button
                            type="button"
                            onClick={() => setShowSequenceModal(true)}
                            style={{
                                padding: '0.6rem 1rem',
                                backgroundColor: '#f0fdf4',
                                color: '#166534',
                                border: '2px solid #86efac',
                                borderRadius: '8px',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.2s',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                            }}
                            title="Configurar número correlativo de comanda y reinicio diario"
                        >
                            <span>🔢</span>
                            <span>Turno / N° Orden</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowHeldOrdersModal(true)}
                        style={{
                            padding: '0.6rem 1.2rem',
                            backgroundColor: heldOrders.length > 0 ? '#fef3c7' : '#f3f4f6',
                            color: heldOrders.length > 0 ? '#b45309' : '#6b7280',
                            border: `2px solid ${heldOrders.length > 0 ? '#f59e0b' : '#d1d5db'}`,
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.6rem',
                            transition: 'all 0.2s',
                            boxShadow: heldOrders.length > 0 ? '0 2px 8px rgba(245, 158, 11, 0.2)' : 'none'
                        }}
                    >
                        <i className="bi bi-pause-circle-fill" style={{ fontSize: '1.1rem', color: heldOrders.length > 0 ? '#f59e0b' : '#9ca3af' }}></i>
                        <span>Órdenes en Espera</span>
                        <span style={{
                            backgroundColor: heldOrders.length > 0 ? '#d97706' : '#9ca3af',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontSize: '0.85rem',
                            fontWeight: 800
                        }}>
                            {heldOrders.length}
                        </span>
                    </button>
                </div>
            </div>

            <div style={{
                display: 'flex',
                flex: 1,
                minHeight: 0, // sin esto la fila crece con el carrito y el pie de cobro se sale de la pantalla
                overflow: 'hidden',
                flexDirection: 'row'
            }}>
                {/* Panel Izquierdo: Catálogo */}
                <div style={{
                    flex: '1 1 60%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#ffffff',
                    borderRight: '2px solid #e5e7eb',
                    minHeight: 'auto',
                    minWidth: 0,
                    maxWidth: '100%'
                }}>
                    {/* Filtros */}
                    <div style={{
                        padding: '1rem',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0,
                        maxWidth: '100%',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '0.75rem',
                            overflowX: 'auto',
                            paddingBottom: '0.25rem',
                            width: '100%'
                        }}>
                            <button
                                style={{
                                    padding: '0.625rem 1.25rem',
                                    borderRadius: '6px',
                                    border: selectedCategory === 'all' ? 'none' : '2px solid #d1d5db',
                                    backgroundColor: selectedCategory === 'all' ? 'var(--primary-color)' : '#ffffff',
                                    color: selectedCategory === 'all' ? '#ffffff' : '#374151',
                                    fontWeight: '600',
                                    fontSize: '0.9375rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                    boxShadow: selectedCategory === 'all' ? '0 2px 4px rgba(100, 16, 14, 0.3)' : 'none',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={() => setSelectedCategory('all')}
                            >
                                Todos los productos
                            </button>
                            {promotions.length > 0 && (
                                <button
                                    style={{
                                        padding: '0.625rem 1.25rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === 'promotions' ? 'none' : '2px solid #fbbf24',
                                        backgroundColor: selectedCategory === 'promotions' ? '#f59e0b' : '#fffbeb',
                                        color: selectedCategory === 'promotions' ? '#ffffff' : '#b45309',
                                        fontWeight: '700',
                                        fontSize: '0.9375rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        boxShadow: selectedCategory === 'promotions' ? '0 2px 4px rgba(245, 158, 11, 0.3)' : 'none',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory('promotions')}
                                >
                                    ⭐ Promociones
                                </button>
                            )}
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    style={{
                                        padding: '0.625rem 1.25rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === cat.id ? 'none' : '2px solid #d1d5db',
                                        backgroundColor: selectedCategory === cat.id ? 'var(--primary-color)' : '#ffffff',
                                        color: selectedCategory === cat.id ? '#ffffff' : '#374151',
                                        fontWeight: '600',
                                        fontSize: '0.9375rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        boxShadow: selectedCategory === cat.id ? '0 2px 4px rgba(100, 16, 14, 0.3)' : 'none',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory(cat.id)}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Grid Productos (más columnas en tablets de 11" en horizontal) */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: screenWidth <= 1366 ? '1rem' : '1.5rem',
                        backgroundColor: 'var(--sidebar-bg)'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(auto-fill, minmax(${screenWidth <= 1366 ? 150 : 180}px, 1fr))`,
                            gap: screenWidth <= 1366 ? '0.75rem' : '1rem'
                        }}>
                            {(selectedCategory === 'promotions' ? promotions : filteredProducts).map(item => (
                                <div
                                    key={item.id}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                    }}
                                    onClick={() => selectedCategory === 'promotions' ? handleAddPromo(item) : handleProductClick(item)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                        e.currentTarget.style.borderColor = selectedCategory === 'promotions' ? '#f59e0b' : 'var(--primary-color)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                    }}
                                >
                                    <div style={{
                                        height: '140px',
                                        backgroundColor: 'var(--sidebar-bg)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.75rem'
                                    }}>
                                        {item.image ? (
                                            <img
                                                src={item.image.startsWith('http') ? item.image : `${process.env.REACT_APP_FAST_FOOD_SERVICE}${item.image}`}
                                                alt={item.name}
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        ) : (
                                            <span style={{
                                                color: '#94a3b8',
                                                fontSize: '0.75rem',
                                                textAlign: 'center'
                                            }}>
                                                Sin imagen
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ padding: '0.875rem' }}>
                                        <h3 style={{
                                            fontSize: '0.9375rem',
                                            fontWeight: '600',
                                            color: selectedCategory === 'promotions' ? '#b45309' : 'var(--primary-color)',
                                            marginBottom: '0.375rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {item.name}
                                        </h3>
                                        {item.description && (
                                            <p style={{
                                                fontSize: '0.75rem',
                                                color: '#6b7280',
                                                marginBottom: '0.375rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}>
                                                {item.description}
                                            </p>
                                        )}
                                        <p style={{
                                            fontSize: '1.125rem',
                                            fontWeight: '700',
                                            color: selectedCategory === 'promotions' ? '#b45309' : 'var(--primary-color)',
                                            margin: 0
                                        }}>
                                            ${item.price}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Panel Derecho: Orden Actual */}
                <div style={{
                    flex: screenWidth <= 1024 ? '0 0 320px' : '0 0 400px',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.05)',
                    flexShrink: 0,
                    minHeight: 0
                }}>
                    {/* Header de Orden Actual */}
                    <div style={{
                        padding: '1rem 1.5rem',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0,
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <h3 style={{
                            fontSize: '1.125rem',
                            fontWeight: '700',
                            color: '#111827',
                            margin: 0
                        }}>
                            Orden Actual
                        </h3>
                    </div>

                    {/* Contenido del Carrito (hace scroll; el pie de cobro queda fijo debajo) */}
                    <div style={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        padding: '1.5rem',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {cart.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '3rem 1rem',
                                color: '#9ca3af',
                                fontSize: '0.875rem'
                            }}>
                                <p style={{ margin: 0 }}>No hay productos en el carrito</p>
                                <p style={{
                                    margin: '0.5rem 0 0 0',
                                    fontSize: '0.8125rem'
                                }}>
                                    Selecciona productos para comenzar
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.75rem',
                                flex: 1
                            }}>
                                {cart.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '10px',
                                            padding: '1rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.75rem'
                                        }}
                                    >
                                        {/* Información del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '0.75rem'
                                        }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{
                                                    fontSize: '0.9375rem',
                                                    fontWeight: '600',
                                                    color: 'var(--primary-color)',
                                                    marginBottom: '0.375rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {item.name}
                                                </h4>
                                                <p style={{
                                                    fontSize: '0.8125rem',
                                                    color: '#6b7280',
                                                    margin: 0
                                                }}>
                                                    {item.discount_percentage > 0 ? (
                                                        <>
                                                            <span style={{ textDecoration: 'line-through', marginRight: '0.25rem', color: '#9ca3af' }}>
                                                                {formatCurrency(item.price)}
                                                            </span>
                                                            <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                                                                {formatCurrency(item.price * (1 - (parseFloat(item.discount_percentage) || 0) / 100))} c/u
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>{formatCurrency(item.price)} c/u</>
                                                    )}
                                                </p>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <button
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#6b7280',
                                                            fontSize: '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, -1)}
                                                    >
                                                        −
                                                    </button>
                                                    <span style={{
                                                        width: '36px',
                                                        textAlign: 'center',
                                                        fontSize: '0.9375rem',
                                                        fontWeight: '600',
                                                        color: 'var(--primary-color)'
                                                    }}>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#6b7280',
                                                            fontSize: '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, 1)}
                                                    >
                                                        +
                                                    </button>
                                                </div>

                                                <button
                                                    style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        backgroundColor: '#fee2e2',
                                                        border: '2px solid #fecaca',
                                                        borderRadius: '8px',
                                                        color: '#dc2626',
                                                        fontSize: '1.125rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    onClick={() => removeFromCart(item)}
                                                    title="Eliminar producto"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>

                                        {/* Nota y descuento del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingTop: '0.75rem',
                                            borderTop: '1px dashed #e5e7eb'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                {item.note ? (
                                                    <div style={{
                                                        fontSize: '0.8125rem',
                                                        color: '#6b7280',
                                                        fontStyle: 'italic',
                                                        backgroundColor: 'var(--sidebar-bg)',
                                                        padding: '0.375rem 0.75rem',
                                                        borderRadius: '4px',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        <strong>Nota:</strong> {item.note}
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        fontSize: '0.8125rem',
                                                        color: '#9ca3af',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Sin notas especiales
                                                    </span>
                                                )}
                                            </div>

                                            {/* Variables seleccionadas */}
                                            {item.selectedVariables && item.selectedVariables.length > 0 && (
                                                <div style={{ padding: '0.25rem 0', width: '100%' }}>
                                                    {item.selectedVariables.map((v, idx) => (
                                                        <div key={idx} style={{
                                                            fontSize: '0.8125rem',
                                                            color: '#4b5563',
                                                            backgroundColor: '#f3f4f6',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            display: 'inline-block',
                                                            marginRight: '4px',
                                                            marginBottom: '4px'
                                                        }}>
                                                            <strong>{v.variable}:</strong> {v.option}
                                                            {v.extra_price > 0 && ` (+$${v.extra_price.toFixed(2)})`}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <span style={{ padding: '0.375rem', backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: '0.8125rem' }}>%</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={item.discount_percentage || ''}
                                                        onChange={(e) => updateItemDiscount(item, e.target.value)}
                                                        placeholder="0"
                                                        style={{
                                                            width: '40px',
                                                            border: 'none',
                                                            padding: '0.375rem',
                                                            textAlign: 'center',
                                                            fontSize: '0.8125rem',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                </div>

                                                <button
                                                    style={{
                                                        padding: '0.375rem 0.75rem',
                                                        backgroundColor: item.note ? '#fef3c7' : 'var(--sidebar-bg)',
                                                        border: `1px solid ${item.note ? '#fbbf24' : '#d1d5db'}`,
                                                        borderRadius: '4px',
                                                        color: item.note ? '#92400e' : '#374151',
                                                        fontSize: '0.8125rem',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap',
                                                        minHeight: TOUCH_MIN_SIZE,
                                                        minWidth: '60px'
                                                    }}
                                                    onClick={() => handleAddNote(item)}
                                                    title={item.note ? "Editar nota" : "Agregar nota"}
                                                >
                                                    {item.note ? '📝 Editar' : '✏️ Nota'}
                                                </button>

                                                {item.max_toppings > 0 && (
                                                    <button
                                                        onClick={() => {
                                                            setActiveToppingsItemIndex(index);
                                                            setShowToppingsModal(true);
                                                        }}
                                                        style={{
                                                            padding: '0.375rem 0.75rem',
                                                            backgroundColor: item.selectedToppings?.length > 0 ? 'var(--primary-color)' : 'var(--sidebar-bg)',
                                                            border: `1px solid ${item.selectedToppings?.length > 0 ? 'var(--primary-color)' : '#d1d5db'}`,
                                                            borderRadius: '4px',
                                                            color: item.selectedToppings?.length > 0 ? '#fff' : '#374151',
                                                            fontSize: '0.8125rem',
                                                            fontWeight: '500',
                                                            cursor: 'pointer',
                                                            whiteSpace: 'nowrap',
                                                            minHeight: TOUCH_MIN_SIZE
                                                        }}
                                                        title="Agregar toppings"
                                                    >
                                                        ➕ Toppings {item.selectedToppings?.length > 0 ? `(${item.selectedToppings.length}/${item.max_toppings})` : ''}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>
                    {/* Totales y Botones: pie fijo del panel (siempre visible; la lista de arriba hace scroll) */}
                    {cart.length > 0 && (
                        <div style={{
                            flexShrink: 0,
                            padding: screenWidth <= 1366 ? '0.75rem 1rem' : '1rem 1.5rem',
                            borderTop: '2px solid #e5e7eb',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)'
                        }}>
                            {/* Totales */}
                            <div style={{
                                paddingBottom: '1rem',
                                marginBottom: '1rem'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '0.5rem',
                                    fontSize: '0.9375rem',
                                    color: '#6b7280'
                                }}>
                                    <span>Subtotal</span>
                                    <span style={{ fontWeight: '600' }}>{currency === 'COP' ? `COP ${(calculateSubtotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateSubtotal)}</span>
                                </div>

                                {appliedDiscount && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.5rem',
                                        fontSize: '0.9375rem',
                                        color: '#dc2626',
                                        fontWeight: '600'
                                    }}>
                                        <span>Descuento</span>
                                        <span>- {formatCurrency(calculateDiscountAmount)}</span>
                                    </div>
                                )}

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    paddingTop: '1rem',
                                    borderTop: '2px solid #e5e7eb',
                                    fontSize: '1.5rem',
                                    fontWeight: '700',
                                    color: '#111827'
                                }}>
                                    <span>Total</span>
                                    <span style={{ color: 'var(--primary-color)' }}>{currency === 'COP' ? `COP ${(calculateTotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateTotal)}</span>
                                </div>
                            </div>

                            {/* Botones Principales */}
                            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                <button
                                    type="button"
                                    style={{
                                        flex: 1,
                                        padding: '1rem 0.5rem',
                                        backgroundColor: '#f59e0b',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: '#ffffff',
                                        fontSize: '1rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={handleHoldCurrentOrder}
                                    title="Poner orden actual en espera para atender otro cliente"
                                >
                                    <i className="bi bi-pause-circle-fill"></i> En Espera
                                </button>

                                <button
                                    type="button"
                                    style={{
                                        flex: 2,
                                        padding: '1rem',
                                        backgroundColor: processingOrder ? '#d1d5db' : 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: '#ffffff',
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        cursor: processingOrder ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        boxShadow: processingOrder ? 'none' : '0 4px 12px rgba(100, 16, 14, 0.3)',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={openOrderConfirmationModal}
                                    disabled={processingOrder}
                                >
                                    {processingOrder ? 'Procesando pedido...' : 'Revisar y Pagar'}
                                </button>
                            </div>

                            {/* 🔓 Botón Abrir Caja */}
                            <button
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    backgroundColor: 'var(--primary-color)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#ffffff',
                                    fontSize: '0.9375rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={handleOpenCashDrawer}
                            >
                                🔓 Abrir Caja Registradora
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    if (loading || checkingShift) return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.125rem',
            color: '#6b7280'
        }}>
            Cargando sistema de punto de venta...
        </div>
    );

    if (!currentShift || !currentShift.id || currentShift.status !== 'open') {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--sidebar-bg)',
                padding: '2rem',
                textAlign: 'center'
            }}>
                <i className="bi bi-lock-fill" style={{ fontSize: '5rem', color: '#dc2626', marginBottom: '1rem' }}></i>
                <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>Caja Cerrada</h2>
                <p style={{ fontSize: '1.25rem', color: '#4b5563', marginBottom: '2.5rem', maxWidth: '600px', lineHeight: '1.5' }}>
                    Debes abrir la caja registradora antes de poder acceder al Punto de Venta y realizar ventas.
                </p>
                <button
                    onClick={() => navigate('/fast-food/shift')}
                    style={{
                        padding: '1rem 2.5rem',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    Ir a Abrir Caja
                </button>
            </div>
        );
    }

    // =====================================
    // 11. ESTRUCTURA PRINCIPAL CON RESPONSIVIDAD
    // =====================================

    // 576px es el breakpoint para móviles, de modo que las tablets usen la vista de escritorio
    const isSmallScreen = screenWidth <= 576;

    return (
        <>
            {/* Vista responsiva basada en el tamaño de pantalla */}
            {isSmallScreen ? renderCompactView() : renderDesktopView()}

            {/* Modal para agregar nota */}
            {editingNoteForItem && renderNoteModal()}

            {/* Modal Confirmación (Compartido) */}
            {showReviewModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: isSmallScreen ? '0.5rem' : '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: isSmallScreen ? '95%' : '550px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            padding: isSmallScreen ? '1rem' : '1.5rem',
                            borderBottom: '2px solid #e5e7eb',
                            backgroundColor: 'var(--primary-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <div>
                                <h3 style={{
                                    fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                                    fontWeight: '700',
                                    color: '#ffffff',
                                    margin: 0
                                }}>
                                    Confirmación de Orden
                                </h3>
                                <p style={{
                                    color: '#d1d5db',
                                    fontSize: isSmallScreen ? '0.75rem' : '0.9rem',
                                    margin: '0.25rem 0 0 0'
                                }}>
                                    Confirma la orden antes de procesar el pago.
                                </p>
                            </div>
                        </div>

                        {renderReviewDetails()}

                        <div style={{
                            padding: isSmallScreen ? '1rem' : '1.5rem',
                            borderTop: '2px solid #e5e7eb',
                            display: 'flex',
                            gap: '0.75rem',
                            justifyContent: 'space-between'
                        }}>
                            <button
                                style={{
                                    padding: isSmallScreen ? '0.75rem' : '0.75rem 1.5rem',
                                    backgroundColor: 'var(--secondary-color)', color: 'var(--primary-color)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#ffffff',
                                    fontWeight: '600',
                                    fontSize: isSmallScreen ? '0.875rem' : '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    flex: 1,
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={() => setShowReviewModal(false)}
                            >
                                Editar Pedido
                            </button>

                            {currentLocalShift?.id && (
                                <button
                                    style={{
                                        padding: isSmallScreen ? '0.75rem' : '0.75rem 1.5rem',
                                        backgroundColor: (!isPaymentValid && paymentMethod === 'dividido') ? '#ef4444' : 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontWeight: '700',
                                        fontSize: isSmallScreen ? '0.875rem' : '1rem',
                                        cursor: (!isPaymentValid || processingOrder) ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        flex: 1,
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => handleInitiatePlaceOrder(false)}
                                    disabled={!isPaymentValid || processingOrder}
                                >
                                    {(!isPaymentValid && paymentMethod === 'dividido') 
                                        ? 'Montos no coinciden' 
                                        : 'Confirmar Pago'}
                                </button>
                            )}

                            {canAccessFeria && currentFeriaShift?.id && (
                                <button
                                    style={{
                                        padding: isSmallScreen ? '0.75rem' : '0.75rem 1.5rem',
                                        backgroundColor: (!isPaymentValid && paymentMethod === 'dividido') ? '#ef4444' : 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontWeight: '700',
                                        fontSize: isSmallScreen ? '0.875rem' : '1rem',
                                        cursor: (!isPaymentValid || processingOrder) ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        flex: 1,
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => handleInitiatePlaceOrder(true)}
                                    disabled={!isPaymentValid || processingOrder}
                                >
                                    {(!isPaymentValid && paymentMethod === 'dividido') 
                                        ? 'Montos no coinciden' 
                                        : 'Pago en Feria'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Crear Cliente (Compartido) */}
            {showCustomerModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: isSmallScreen ? '0.5rem' : '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: isSmallScreen ? '95%' : '500px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            padding: isSmallScreen ? '1rem' : '1.5rem',
                            borderBottom: '2px solid #e5e7eb',
                            backgroundColor: 'var(--sidebar-bg)'
                        }}>
                            <h3 style={{
                                fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                                fontWeight: '700',
                                color: '#111827',
                                margin: 0
                            }}>
                                Nuevo Cliente
                            </h3>
                        </div>

                        <form onSubmit={handleCreateCustomer} style={{ padding: isSmallScreen ? '1rem' : '1.5rem' }}>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Cédula / RUC (Identificación)
                                </label>
                                <input
                                    type="text"
                                    name="cedula"
                                    value={newCustomer.cedula}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        transition: 'all 0.2s',
                                        boxSizing: 'border-box',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Correo Electrónico
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={newCustomer.email}
                                    onChange={handleInputChange}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        transition: 'all 0.2s',
                                        boxSizing: 'border-box',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                />
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '0.75rem',
                                marginBottom: '1rem'
                            }}>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                        fontWeight: '600',
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Nombre
                                    </label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={newCustomer.first_name}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                            border: '2px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                            transition: 'all 0.2s',
                                            boxSizing: 'border-box',
                                            minHeight: TOUCH_MIN_SIZE
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                        fontWeight: '600',
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Apellido
                                    </label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={newCustomer.last_name}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                            border: '2px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                            transition: 'all 0.2s',
                                            boxSizing: 'border-box',
                                            minHeight: TOUCH_MIN_SIZE
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Teléfono
                                </label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={newCustomer.phone}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        transition: 'all 0.2s',
                                        boxSizing: 'border-box',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Ciudad
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    value={newCustomer.city}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        transition: 'all 0.2s',
                                        boxSizing: 'border-box',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                />
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: '0.75rem',
                                justifyContent: 'flex-end',
                                borderTop: '2px solid #e5e7eb',
                                paddingTop: '1rem'
                            }}>
                                <button
                                    type="button"
                                    style={{
                                        padding: isSmallScreen ? '0.5rem 1rem' : '0.75rem 1.5rem',
                                        backgroundColor: '#ffffff',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        color: '#374151',
                                        fontWeight: '600',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setShowCustomerModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        padding: isSmallScreen ? '0.5rem 1rem' : '0.75rem 1.5rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontWeight: '600',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                >
                                    Guardar Cliente
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* Modal de Toppings */}
            {showToppingsModal && activeToppingsItemIndex !== null && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '400px',
                        padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                        maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: 0, color: '#111827', fontSize: '1.25rem' }}>Seleccionar Toppings</h3>
                            <button 
                                onClick={() => setShowToppingsModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#9ca3af', cursor: 'pointer' }}
                            >×</button>
                        </div>
                        
                        <p style={{ color: '#4b5563', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            Puedes seleccionar hasta {cart[activeToppingsItemIndex].max_toppings} toppings.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {availableToppings.map(topping => {
                                const activeItem = cart[activeToppingsItemIndex];
                                const count = activeItem.selectedToppings?.filter(t => t === topping.name).length || 0;
                                const canSelectMore = (activeItem.selectedToppings?.length || 0) < activeItem.max_toppings;
                                
                                return (
                                    <div 
                                        key={topping.id}
                                        style={{
                                            padding: '0.75rem 1rem',
                                            borderRadius: '8px',
                                            border: `1px solid #e5e7eb`,
                                            backgroundColor: count > 0 ? '#fef2f2' : '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <span style={{ fontWeight: count > 0 ? 'bold' : 'normal', color: '#111827' }}>
                                            {topping.name}
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <button
                                                onClick={() => {
                                                    if (count > 0) {
                                                        const newCart = [...cart];
                                                        const currentToppings = newCart[activeToppingsItemIndex].selectedToppings || [];
                                                        const indexToRemove = currentToppings.lastIndexOf(topping.name);
                                                        if (indexToRemove > -1) {
                                                            currentToppings.splice(indexToRemove, 1);
                                                        }
                                                        newCart[activeToppingsItemIndex].selectedToppings = currentToppings;
                                                        setCart(newCart);
                                                    }
                                                }}
                                                style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', cursor: count > 0 ? 'pointer' : 'not-allowed', opacity: count > 0 ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                                            >-</button>
                                            <span style={{ minWidth: '1.5rem', textAlign: 'center', fontWeight: '600' }}>{count}</span>
                                            <button
                                                onClick={() => {
                                                    if (canSelectMore) {
                                                        const newCart = [...cart];
                                                        const currentToppings = newCart[activeToppingsItemIndex].selectedToppings || [];
                                                        currentToppings.push(topping.name);
                                                        newCart[activeToppingsItemIndex].selectedToppings = currentToppings;
                                                        setCart(newCart);
                                                    } else {
                                                        alert(`Límite alcanzado: máximo ${activeItem.max_toppings} toppings.`);
                                                    }
                                                }}
                                                style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', cursor: canSelectMore ? 'pointer' : 'not-allowed', opacity: canSelectMore ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                                            >+</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => setShowToppingsModal(false)}
                                style={{
                                    padding: '0.75rem 1.5rem', backgroundColor: 'var(--primary-color)',
                                    color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                                }}
                            >
                                Listo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Variables */}
            {showVariablesModal && selectedProductForVariables && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '12px', padding: '2rem',
                        width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: 0, color: '#111827' }}>Personalizar {selectedProductForVariables.product.name}</h3>
                            <button onClick={() => setShowVariablesModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
                        </div>
                        
                        {selectedProductForVariables.product.variables.map(variable => {
                            const isRequired = variable.is_required !== false;
                            return (
                                <div key={variable.id} style={{ marginBottom: '1.5rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                        <h4 style={{ margin: 0, color: '#374151', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <span>{variable.name}</span>
                                            {isRequired ? (
                                                <span style={{ color: '#ef4444', fontWeight: 'bold' }}>* <span style={{ fontSize: '0.75rem', color: '#b91c1c' }}>(Obligatoria)</span></span>
                                            ) : (
                                                <span style={{ color: '#2563eb', fontSize: '0.75rem', fontWeight: 'normal', backgroundColor: '#dbeafe', padding: '0.1rem 0.5rem', borderRadius: '999px' }}>Opcional</span>
                                            )}
                                        </h4>
                                        {!isRequired && currentVariablesSelection[variable.id] && (
                                            <button 
                                                type="button"
                                                onClick={() => setCurrentVariablesSelection(prev => {
                                                    const next = { ...prev };
                                                    delete next[variable.id];
                                                    return next;
                                                })}
                                                style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}
                                            >
                                                Quitar selección
                                            </button>
                                        )}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.5rem' }}>
                                        {variable.options?.filter(o => o.is_active).map(option => {
                                            const isSelected = currentVariablesSelection[variable.id]?.id === option.id;
                                            return (
                                                <div 
                                                    key={option.id}
                                                    onClick={() => {
                                                        setCurrentVariablesSelection(prev => {
                                                            // Si es opcional y ya estaba seleccionada, un nuevo click la deselecciona
                                                            if (isSelected && !isRequired) {
                                                                const next = { ...prev };
                                                                delete next[variable.id];
                                                                return next;
                                                            }
                                                            return { ...prev, [variable.id]: option };
                                                        });
                                                    }}
                                                    style={{
                                                        padding: '0.75rem',
                                                        border: `2px solid ${isSelected ? 'var(--primary-color)' : '#e5e7eb'}`,
                                                        borderRadius: '8px',
                                                        backgroundColor: isSelected ? '#eff6ff' : '#fff',
                                                        cursor: 'pointer',
                                                        textAlign: 'center',
                                                        fontWeight: isSelected ? 'bold' : 'normal',
                                                        color: isSelected ? 'var(--primary-color)' : '#374151',
                                                        transition: 'all 0.15s ease'
                                                    }}
                                                >
                                                    <div>{option.name}</div>
                                                    {parseFloat(option.extra_price) > 0 && (
                                                        <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: isSelected ? 'var(--primary-color)' : '#6b7280' }}>
                                                            +${parseFloat(option.extra_price).toFixed(2)}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}

                        <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button 
                                onClick={() => setShowVariablesModal(false)}
                                style={{
                                    padding: '0.75rem 1.5rem', backgroundColor: '#e5e7eb',
                                    color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                                }}
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmVariablesSelection}
                                style={{
                                    padding: '0.75rem 1.5rem', backgroundColor: 'var(--primary-color)',
                                    color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                                }}
                            >
                                Confirmar y Agregar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Órdenes en Espera (Held / Parked Orders) */}
            {showHeldOrdersModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1100,
                    padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '14px', width: '100%',
                        maxWidth: '650px', maxHeight: '85vh', overflow: 'hidden',
                        display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
                    }}>
                        {/* Header Modal */}
                        <div style={{
                            padding: '1.25rem 1.5rem', backgroundColor: '#fef3c7',
                            borderBottom: '2px solid #fde68a', display: 'flex',
                            justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <i className="bi bi-pause-circle-fill" style={{ fontSize: '1.5rem', color: '#d97706' }}></i>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#92400e', fontWeight: '700' }}>
                                        Órdenes en Espera
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#b45309' }}>
                                        Selecciona una orden para recuperarla y continuar con el cobro
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowHeldOrdersModal(false)}
                                style={{
                                    background: 'none', border: 'none', fontSize: '1.5rem',
                                    cursor: 'pointer', color: '#92400e'
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Contenido Modal */}
                        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', flex: 1 }}>
                            {heldOrders.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
                                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#d1d5db', display: 'block', marginBottom: '0.5rem' }}></i>
                                    <h4 style={{ margin: '0 0 0.25rem 0', color: '#374151' }}>No hay órdenes en espera</h4>
                                    <p style={{ margin: 0, fontSize: '0.85rem' }}>
                                        Cuando un cliente tarde en decidir o pagar, presiona "⏸️ En Espera" en el carrito para guardarlo aquí y atender al siguiente.
                                    </p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {heldOrders.map((ho) => {
                                        const customerName = ho.selectedCustomer ? `${ho.selectedCustomer.first_name || ''} ${ho.selectedCustomer.last_name || ''}`.trim() : (ho.customerSearch || 'Consumidor Final');
                                        const itemCount = ho.cart ? ho.cart.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0;
                                        return (
                                            <div
                                                key={ho.id}
                                                style={{
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '10px',
                                                    padding: '1rem',
                                                    backgroundColor: '#fafafa',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: '1rem',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                                        <span style={{
                                                            backgroundColor: '#e0f2fe', color: '#0369a1',
                                                            padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700'
                                                        }}>
                                                            {ho.id}
                                                        </span>
                                                        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                                            🕒 Guardada: {ho.held_at}
                                                        </span>
                                                        {ho.selectedTable && (
                                                            <span style={{ fontSize: '0.8rem', color: '#15803d', fontWeight: '600' }}>
                                                                🪑 Mesa: {ho.selectedTable}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '0.95rem' }}>
                                                        👤 {customerName}
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: '0.25rem' }}>
                                                        {itemCount} producto{itemCount > 1 ? 's' : ''}: {ho.cart?.map(i => `${i.quantity}x ${i.name}`).join(', ')}
                                                    </div>
                                                    <div style={{ fontSize: '1rem', fontWeight: '800', color: 'var(--primary-color)', marginTop: '0.35rem' }}>
                                                        Total: {ho.currency === 'COP' ? `COP ${(ho.total * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(ho.total)}
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRestoreHeldOrder(ho)}
                                                        style={{
                                                            padding: '0.6rem 1rem',
                                                            backgroundColor: 'var(--primary-color)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            fontWeight: '700',
                                                            fontSize: '0.875rem',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        <i className="bi bi-play-fill"></i> Recuperar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleDeleteHeldOrder(ho.id, e)}
                                                        style={{
                                                            padding: '0.6rem 0.75rem',
                                                            backgroundColor: '#fee2e2',
                                                            color: '#b91c1c',
                                                            border: '1px solid #fca5a5',
                                                            borderRadius: '8px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer'
                                                        }}
                                                        title="Eliminar orden en espera"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer Modal */}
                        <div style={{
                            padding: '1rem 1.5rem', backgroundColor: '#f9fafb',
                            borderTop: '1px solid #e5e7eb', display: 'flex',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowHeldOrdersModal(false)}
                                style={{
                                    padding: '0.6rem 1.25rem', backgroundColor: '#e5e7eb',
                                    color: '#374151', border: 'none', borderRadius: '8px',
                                    fontWeight: '600', cursor: 'pointer'
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Overlay animado de carga durante facturación e impresión */}
            {renderPrintOverlay()}

            {/* Modal para selección de tipo de comprobante (Factura SRI vs Nota de Entrega) */}
            {showDocTypeModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9998, padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '20px',
                        width: '100%',
                        maxWidth: '460px',
                        padding: '2rem 1.75rem',
                        boxShadow: '0 25px 60px -10px rgba(0, 0, 0, 0.35)',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🧾</div>
                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.4rem', fontWeight: '800', color: '#111827' }}>
                            Tipo de Comprobante
                        </h3>
                        <p style={{ color: '#6b7280', fontSize: '0.95rem', marginBottom: '1.75rem', lineHeight: '1.4' }}>
                            Selecciona cómo deseas procesar e imprimir esta venta:
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setDocumentType('factura');
                                    finalPlaceOrder(pendingIsFeria, 'factura');
                                }}
                                style={{
                                    padding: '1.1rem 1rem',
                                    borderRadius: '12px',
                                    backgroundColor: '#0d47a1',
                                    color: '#ffffff',
                                    border: 'none',
                                    fontWeight: '700',
                                    fontSize: '1.05rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '5px',
                                    boxShadow: '0 4px 12px rgba(13, 71, 161, 0.3)',
                                    transition: 'all 0.2s',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0d47a1'}
                            >
                                <span style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    🧾 Factura Electrónica
                                </span>
                                <span style={{ fontSize: '0.8rem', opacity: 0.9, fontWeight: 'normal' }}>
                                    Emite y autoriza en el SRI (Validez tributaria)
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setDocumentType('nota_entrega');
                                    finalPlaceOrder(pendingIsFeria, 'nota_entrega');
                                }}
                                style={{
                                    padding: '1.1rem 1rem',
                                    borderRadius: '12px',
                                    backgroundColor: '#166534',
                                    color: '#ffffff',
                                    border: 'none',
                                    fontWeight: '700',
                                    fontSize: '1.05rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '5px',
                                    boxShadow: '0 4px 12px rgba(22, 101, 52, 0.3)',
                                    transition: 'all 0.2s',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#166534'}
                            >
                                <span style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    📋 Nota de Venta / Entrega
                                </span>
                                <span style={{ fontSize: '0.8rem', opacity: 0.9, fontWeight: 'normal' }}>
                                    Documento interno (Sin validez tributaria SRI)
                                </span>
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowDocTypeModal(false)}
                            style={{
                                padding: '0.7rem 1.5rem',
                                backgroundColor: '#f3f4f6',
                                color: '#4b5563',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                minHeight: TOUCH_MIN_SIZE
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
            {/* Modal de Configuración de Secuencia y Reinicio Diario */}
            <ConfiguracionSecuenciaModal
                isOpen={showSequenceModal}
                onClose={() => setShowSequenceModal(false)}
            />
            {/* Aviso flotante de venta enviada (reemplaza al alert para no cortar KOAPrint en Android) */}
            {orderSuccessNotification && (
                <div role="status" style={{
                    position: 'fixed', bottom: '24px', right: '24px',
                    backgroundColor: '#10b981', color: '#ffffff',
                    padding: '1rem 1.5rem', borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    zIndex: 3000, fontWeight: 'bold', fontSize: '1.05rem'
                }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                    <span>{orderSuccessNotification}</span>
                </div>
            )}
            {/* Canje de regalos por puntos (cuenta en el turno abierto: normal o de feria) */}
            <RegalosModal
                isOpen={showRegalosModal}
                onClose={() => setShowRegalosModal(false)}
                isFeria={!currentLocalShift && !!currentFeriaShift}
            />
        </>
    );
};

export default PuntosVenta;